import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  FiUser,
  FiPackage,
  FiCreditCard,
  FiCheck,
  FiX,
  FiInfo,
} from "react-icons/fi";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { toast } from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { authApi, useGetMe } from "../../../api/authApi";
import { billingApi } from "../../../api/billingApi";
import {
  usePaymentMethods,
  useDeletePaymentMethod,
  useSetDefaultPaymentMethod,
} from "../../../api/billingApi";
import AddPaymentCardDialog from "./AddPaymentCardDialog";
import { IReservation } from "../../../interfaces/Ipackages";
import { IPaymentMethodsResponse } from "../../../interfaces/Ibilling";
import {
  getPackageTypeFromPhoneNumber,
  ensurePhoneNumberFormat,
  formatPhoneNumberForDisplay,
} from "../../../utils/phoneNumberUtils";
import AddressAutocomplete from "../../../components/AddressAutocomplete";
import {
  validateEmail,
  validatePhone,
  validateRequired,
  validateFields,
  areAllValid,
} from "../../../utils/validation";
import usFlagImage from "../../../assets/images/usflag.png";

// Utility function to calculate remaining time until expiration
const calculateRemainingTime = (
  expiresAt: string,
  currentTime: Date = new Date()
) => {
  const expirationDate = new Date(expiresAt);
  const timeDifference = expirationDate.getTime() - currentTime.getTime();

  if (timeDifference <= 0) {
    return { days: 0, hours: 0, minutes: 0, isExpired: true };
  }

  const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));

  return { days, hours, minutes, isExpired: false };
};

interface UserStateInfo {
  identity_data_completion_needed: boolean;
  package_selection_needed: boolean;
  has_reserved: null | IReservation;
  has_payment_card: null | IPaymentMethodsResponse;
}

interface UserStateStepperProps {
  isOpen: boolean;
  onComplete: () => void;
  userStateInfo: UserStateInfo;
  onUserStateUpdate: (updatedState: UserStateInfo) => void;
}

type StepType =
  | "personal_data"
  | "package_selection"
  | "reservation"
  | "payment_card"
  | "complete";

interface Step {
  id: StepType;
  title: string;
  description: string;
  icon: React.ReactNode;
  isActive: boolean;
  isCompleted: boolean;
}

export default function UserStateStepper({
  isOpen,
  onComplete,
  userStateInfo,
  onUserStateUpdate,
}: UserStateStepperProps) {
  const [currentStep, setCurrentStep] = useState<StepType>("personal_data");
  const [isLoading, setIsLoading] = useState(false);

  // Query client for manual cache invalidation
  const queryClient = useQueryClient();

  // Fetch current user data
  const { data: userData } = useGetMe();

  // Clean up Stripe redirect query parameters on mount
  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const hasStripeParams =
      currentUrl.searchParams.has("setup_intent") ||
      currentUrl.searchParams.has("setup_intent_client_secret") ||
      currentUrl.searchParams.has("redirect_status");

    if (hasStripeParams) {
      console.log("🧹 Cleaning up Stripe redirect parameters from URL");
      // Remove Stripe-related query parameters
      currentUrl.searchParams.delete("setup_intent");
      currentUrl.searchParams.delete("setup_intent_client_secret");
      currentUrl.searchParams.delete("redirect_status");

      // Update URL without page refresh
      window.history.replaceState(
        {},
        document.title,
        currentUrl.pathname + currentUrl.search
      );
    }
  }, []); // Only run once on mount

  // Create a static copy of userStateInfo at the beginning to prevent steps from changing
  // This ensures that the step flow remains consistent throughout the user's session
  // and doesn't get disrupted by userStateInfo updates during step completion
  const [staticUserStateInfo, setStaticUserStateInfo] =
    useState<UserStateInfo>(userStateInfo);

  // Function to manually refresh the static user state info when needed
  // This is called explicitly when we want to change the step flow (e.g., going back to package selection)
  // NOT called after completing steps to keep header steps fixed
  const refreshStaticUserState = useCallback((newUserState: UserStateInfo) => {
    setStaticUserStateInfo(newUserState);
  }, []);

  // Update static copy only when component first mounts or when explicitly needed
  useEffect(() => {
    setStaticUserStateInfo(userStateInfo);
  }, []); // Empty dependency array means this only runs once on mount

  // Personal data form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // Business data fields
  const [businessName, setBusinessName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [line1, setLine1] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [country, setCountry] = useState("USA"); // Default to USA
  const [phone, setPhone] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");

  // Autofill personal data fields when user data is available
  useEffect(() => {
    if (userData?.data) {
      console.log("Current user data:", userData.data);

      const user = userData.data;

      // Autofill firstName if available (from profile.first_name)
      if (user.profile?.first_name && !firstName) {
        setFirstName(user.profile.first_name);
      }

      // Autofill lastName if available (from profile.last_name)
      if (user.profile?.last_name && !lastName) {
        setLastName(user.profile.last_name);
      }

      // Autofill businessEmail if available (from root email)
      if (user.email && !businessEmail) {
        setBusinessEmail(user.email);
      }
    }
  }, [userData, firstName, lastName, businessEmail]);

  // Debug button state
  useEffect(() => {
    console.log("Form state:", {
      firstName,
      lastName,
      agreeToTerms,
      businessName,
      city,
      state,
      line1,
      zipCode,
      country,
      phone,
      businessEmail,
      isButtonDisabled:
        isLoading ||
        !firstName ||
        !lastName ||
        !agreeToTerms ||
        !businessName ||
        !city ||
        !state ||
        !line1 ||
        !zipCode ||
        !country ||
        !validatePhone(phone).isValid ||
        !businessEmail,
      phoneValidation: validatePhone(phone),
    });
  }, [
    firstName,
    lastName,
    agreeToTerms,
    businessName,
    city,
    state,
    line1,
    zipCode,
    country,
    phone,
    businessEmail,
    isLoading,
  ]);
  const [fullAddress, setFullAddress] = useState("");

  // Validation state
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Handle address selection from autocomplete
  const handleAddressSelect = (addressData: {
    line1: string;
    city: string;
    state: string;
    zipCode: string;
    country?: string;
  }) => {
    setLine1(addressData.line1);
    setCity(addressData.city);
    setState(addressData.state);
    setZipCode(addressData.zipCode);
    // Set country from address data or default to USA
    setCountry(addressData.country || "USA");

    // Clear validation errors for address fields when address is selected
    clearFieldError("line1");
    clearFieldError("city");
    clearFieldError("state");
    clearFieldError("zipCode");
  };

  // Validate form fields
  const validateForm = () => {
    const validations = validateFields([
      {
        field: "firstName",
        value: firstName,
        validator: (value) => validateRequired(value, "First name"),
      },
      {
        field: "lastName",
        value: lastName,
        validator: (value) => validateRequired(value, "Last name"),
      },
      {
        field: "businessName",
        value: businessName,
        validator: (value) => validateRequired(value, "Company name"),
      },
      {
        field: "phone",
        value: phone,
        validator: validatePhone,
      },
      {
        field: "businessEmail",
        value: businessEmail,
        validator: validateEmail,
      },
      {
        field: "city",
        value: city,
        validator: (value) => validateRequired(value, "City"),
      },
      {
        field: "state",
        value: state,
        validator: (value) => validateRequired(value, "State"),
      },
      {
        field: "line1",
        value: line1,
        validator: (value) => validateRequired(value, "Address"),
      },
      {
        field: "zipCode",
        value: zipCode,
        validator: (value) => validateRequired(value, "Zip code"),
      },
      {
        field: "country",
        value: country,
        validator: (value) => validateRequired(value, "Country"),
      },
    ]);

    const errors: Record<string, string> = {};
    Object.entries(validations).forEach(([field, result]) => {
      if (!result.isValid && result.error) {
        errors[field] = result.error;
      }
    });

    setValidationErrors(errors);
    setShowValidationErrors(true);
    return areAllValid(validations);
  };

  // Clear validation error for a specific field
  const clearFieldError = (fieldName: string) => {
    if (validationErrors[fieldName]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Package selection state
  const [selectedType, setSelectedType] = useState<"regular" | "toll_free">(
    "regular"
  );
  const [selectedNumber, setSelectedNumber] = useState<string | null>(null);
  const [regularNumbers, setRegularNumbers] = useState<any[]>([]);
  const [tollFreeNumbers, setTollFreeNumbers] = useState<string[]>([]);
  const [regularPackages, setRegularPackages] = useState<any[]>([]);
  const [tollFreePackages, setTollFreePackages] = useState<any[]>([]);
  const [selectedPackageIndex, setSelectedPackageIndex] = useState(0);

  // Test numbers for development
  const testRegularNumbers = [
    {
      full_number: "+19495292627",
      rate_center: "tehran",
      country_code: "ir",
      city: "tehran",
      state: "tehran",
    },
    {
      full_number: "+13373859858",
      rate_center: "tehran",
      country_code: "ir",
      city: "tehran",
      state: "tehran",
    },
    {
      full_number: "+13375950009",
      rate_center: "tehran",
      country_code: "ir",
      city: "tehran",
      state: "tehran",
    },
  ];
  const testTollFreeNumbers = ["8334174691", "8334174692", "8334174693"];
  const [selectedPackage, setSelectedPackage] = useState<any>(null);

  // Payment card state
  const [showAddCard, setShowAddCard] = useState(false);
  const [cardAdded, setCardAdded] = useState(false);
  const [newlyAddedCard, setNewlyAddedCard] = useState<any>(null);
  const [isReplacingCard, setIsReplacingCard] = useState(false);
  const [isReplacingCardLoading, setIsReplacingCardLoading] = useState(false);
  const [showReplaceConfirmation, setShowReplaceConfirmation] = useState(false);
  const [currentCardId, setCurrentCardId] = useState<string | undefined>(
    undefined
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false); // Track if any dialog is open

  // Payment result state
  const [paymentResultData, setPaymentResultData] = useState<any>(null);
  const [completedPackageInfo, setCompletedPackageInfo] = useState<any>(null);
  const [isNavigatingToComplete, setIsNavigatingToComplete] = useState(false);

  // Store personal data temporarily (defined early for use in getSteps)
  const [tempPersonalData, setTempPersonalData] = useState<{
    firstName: string;
    lastName: string;
    businessName: string;
    city: string;
    state: string;
    line1: string;
    zipCode: string;
    country: string;
    phone: string;
    businessEmail: string;
  } | null>(null);

  // Store package selection data temporarily (defined early for use in getSteps)
  const [tempPackageData, setTempPackageData] = useState<{
    selectedType: "regular" | "toll_free";
    selectedNumber: string;
    selectedPackage: any;
    selectedPackageIndex: number;
  } | null>(null);

  // Payment methods state
  const { data: cards = [], refetch: refetchCards } = usePaymentMethods();
  const { mutate: deletePaymentMethod } = useDeletePaymentMethod();
  const { mutate: setDefaultPaymentMethod } = useSetDefaultPaymentMethod();

  // Debug payment methods
  useEffect(() => {
    console.log("🔍 DEBUG - Payment methods loaded:", cards);
    console.log("🔍 DEBUG - Number of payment methods:", cards.length);
    if (cards.length > 0) {
      console.log(
        "🔍 DEBUG - Payment methods details:",
        cards.map((card: any) => ({
          id: card.id,
          last4: card.card?.last4,
          brand: card.card?.brand,
          is_default: card.is_default,
        }))
      );
    }
  }, [cards]);

  // Reservation management state
  const [showReservationActions, setShowReservationActions] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showPackageChange, setShowPackageChange] = useState(false);
  const [selectedNewPackage, setSelectedNewPackage] = useState<any>(null);
  const [isChangingPackage, setIsChangingPackage] = useState(false);
  const [isDeletingReservation, setIsDeletingReservation] = useState(false);
  const [availablePackages, setAvailablePackages] = useState<any[]>([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);

  // Handle reservation actions
  const handleShowReservationActions = (
    e?: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowReservationActions(true);
  };

  const handleDeleteReservation = async () => {
    setIsDeletingReservation(true);
    try {
      const { packagesApi } = await import("../../../api/packagesApi");
      await packagesApi.deleteReservation();

      toast.success("Reservation deleted successfully");
      setShowDeleteConfirmation(false);
      setShowReservationActions(false);
      setIsDeletingReservation(false);

      // Reset reservation-related state
      setSelectedNumber("");
      setSelectedPackageIndex(0);

      // Update user state to remove reservation and indicate package selection is needed
      const updatedState = {
        ...userStateInfo,
        has_reserved: null,
        package_selection_needed: true, // User needs to select package again
      };
      onUserStateUpdate(updatedState);
      // Update static state here because we're changing the flow (going back to package selection)
      // This is one of the few cases where we want to update the header steps
      refreshStaticUserState(updatedState);

      // Navigate to package selection step to choose another phone and package
      setCurrentStep("package_selection");
    } catch (error) {
      console.error("Error deleting reservation:", error);
      toast.error("Failed to delete reservation");
      setIsDeletingReservation(false);
    }
  };

  const handleChangePackage = async () => {
    if (!userStateInfo.has_reserved?.phone_number) {
      toast.error("No reserved phone number found");
      return;
    }

    setIsLoadingPackages(true);
    try {
      // Determine package type based on reserved phone number
      const packageType = getPackageTypeFromPhoneNumber(
        userStateInfo.has_reserved.phone_number
      );

      // Fetch packages for the determined type
      const response = await authApi.getPackages(packageType);
      setAvailablePackages(response.data.packages || []);

      setShowPackageChange(true);
      setShowReservationActions(false);
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("Failed to fetch available packages");
    } finally {
      setIsLoadingPackages(false);
    }
  };

  const handlePackageChangeConfirm = async () => {
    if (!selectedNewPackage) {
      toast.error("Please select a new package");
      return;
    }

    if (!userStateInfo.has_reserved?.reservation_id) {
      toast.error("No reservation found to update");
      return;
    }

    setIsChangingPackage(true);
    try {
      // Update the existing reservation with the new package
      const { packagesApi } = await import("../../../api/packagesApi");
      await packagesApi.updateReservation(
        userStateInfo.has_reserved.reservation_id,
        selectedNewPackage.id
      );

      toast.success("Package changed successfully!");
      setShowPackageChange(false);
      setSelectedNewPackage(null);
      setAvailablePackages([]);
      setIsChangingPackage(false);

      // Update user state with the new package
      if (userStateInfo.has_reserved) {
        const updatedUserState = {
          ...userStateInfo,
          has_reserved: {
            ...userStateInfo.has_reserved,
            package: selectedNewPackage,
          },
        };

        // Set flag to prevent useEffect from interfering with our navigation
        setIsChangingPackage(true);

        // After package change, navigate to the appropriate step instead of closing
        // Check if user needs to complete payment for the new package
        if (!userStateInfo.has_payment_card) {
          // User needs to add payment card
          setCurrentStep("payment_card");
        } else {
          // User has payment card, go to reservation step to complete payment
          setCurrentStep("reservation");
        }

        console.log(
          "🎯 Package changed successfully, navigating to next step:",
          currentStep
        );

        // Update user state after setting the step
        onUserStateUpdate(updatedUserState);
        // Update static state here because we're changing the flow (changing package)
        // This is one of the few cases where we want to update the header steps
        refreshStaticUserState(updatedUserState);
      }
    } catch (error) {
      console.error("Error changing package:", error);
      toast.error("Failed to change package");
      setIsChangingPackage(false);
    }
  };

  // Determine steps based on static user state (not the dynamic userStateInfo)
  // This prevents steps from changing unexpectedly during the user's session
  // Steps are only recalculated when explicitly calling refreshStaticUserState for flow changes
  // NOT after completing individual steps
  const getSteps = (): Step[] => {
    const steps: Step[] = [];

    // Debug logging
    console.log("getSteps - staticUserStateInfo:", staticUserStateInfo);
    console.log(
      "identity_data_completion_needed:",
      staticUserStateInfo.identity_data_completion_needed
    );
    console.log(
      "package_selection_needed:",
      staticUserStateInfo.package_selection_needed
    );
    console.log("has_reserved:", staticUserStateInfo.has_reserved);
    console.log("has_payment_card:", staticUserStateInfo.has_payment_card);
    console.log("!has_payment_card:", !staticUserStateInfo.has_payment_card);

    // Define step order for completed state
    const stepOrder: StepType[] = [
      "personal_data",
      "package_selection",
      "payment_card",
      "complete",
    ];
    const getCurrentStepOrder = () => stepOrder.indexOf(currentStep);

    if (staticUserStateInfo.identity_data_completion_needed) {
      steps.push({
        id: "personal_data",
        title: "Personal Data",
        description: "Complete your profile",
        icon: <FiUser size={20} />,
        isActive: currentStep === "personal_data",
        isCompleted: getCurrentStepOrder() > stepOrder.indexOf("personal_data"),
      });
    }

    if (staticUserStateInfo.package_selection_needed) {
      steps.push({
        id: "package_selection",
        title: "Select Package",
        description: "Choose number & plan",
        icon: <FiPackage size={20} />,
        isActive: currentStep === "package_selection",
        isCompleted:
          getCurrentStepOrder() > stepOrder.indexOf("package_selection"),
      });
    }

    // Always add payment_card step (Step 3)
    if (staticUserStateInfo.package_selection_needed) {
      console.log("Adding payment_card step (Step 3)");
      steps.push({
        id: "payment_card",
        title: "Payment Method",
        description: "Add card & complete setup",
        icon: <FiCreditCard size={20} />,
        isActive: currentStep === "payment_card",
        isCompleted: getCurrentStepOrder() > stepOrder.indexOf("payment_card"),
      });
    }

    // Always add complete step as the final step (Step 4)
    if (staticUserStateInfo.package_selection_needed) {
      console.log("Adding complete step (final step - Step 4)");
      steps.push({
        id: "complete",
        title: "Complete",
        description: "Setup finished",
        icon: <FiCheck size={20} />,
        isActive: currentStep === "complete",
        isCompleted: false,
      });
    }

    console.log("Final steps (4 steps):", steps);
    console.log("Steps length:", steps.length);
    console.log("Current step will be:", currentStep);

    return steps;
  };

  // Step state variable to manage just header stepper and steps
  // Uses staticUserStateInfo instead of userStateInfo to prevent unwanted step changes
  // Static state is only updated when we want to change the flow, not after completing steps
  const steps = useMemo(
    () => getSteps(),
    [
      staticUserStateInfo,
      currentStep,
      isNavigatingToComplete,
      isChangingPackage,
      tempPackageData,
    ]
  );

  // Header-specific step state for optimized rendering
  const headerSteps = useMemo(
    () =>
      steps.map((step) => ({
        id: step.id,
        title: step.title,
        description: step.description,
        icon: step.icon,
        isActive: step.isActive,
        isCompleted: step.isCompleted,
        index: steps.indexOf(step),
      })),
    [steps]
  );

  // Debug logging for userStateInfo changes
  useEffect(() => {
    console.log("🔍 UserStateStepper - userStateInfo changed:", userStateInfo);
    console.log("🔍 Current step:", currentStep);
    console.log("🔍 Steps length:", steps?.length || 0);

    // Ensure we're on complete step when isNavigatingToComplete is true
    if (isNavigatingToComplete && currentStep !== "complete") {
      console.log(
        "🔍 Setting current step to complete due to isNavigatingToComplete flag"
      );
      setCurrentStep("complete");
    }
  }, [
    userStateInfo,
    currentStep,
    steps,
    isNavigatingToComplete,
    isChangingPackage,
  ]);

  // Initialize current step
  useEffect(() => {
    console.log("useEffect - steps changed, currentStep:", currentStep);
    console.log(
      "Available steps:",
      steps.map((s) => s.id)
    );

    // Don't reset step if a dialog is open
    if (isDialogOpen) {
      console.log("Dialog is open, preventing step reset");
      return;
    }

    if (steps.length > 0) {
      // Check if current step is still valid in the new steps array
      const currentStepExists = steps.some((step) => step.id === currentStep);

      if (currentStepExists) {
        // Keep the current step if it's still valid
        console.log("Current step is still valid, keeping:", currentStep);
        return;
      } else {
        // Don't reset to first step if we're intentionally on "complete" step or changing package
        // Also don't reset if we're on payment_card step (to prevent issues when opening dialog)
        if (
          currentStep === "complete" ||
          currentStep === "payment_card" ||
          isNavigatingToComplete ||
          isChangingPackage
        ) {
          console.log(
            "Keeping current step, not resetting (complete/payment_card/navigating/changing package)"
          );
          return;
        }

        // Only reset to first step if current step is no longer valid
        console.log("Current step no longer valid, resetting to:", steps[0].id);
        setCurrentStep(steps[0].id);
      }
    }
  }, [
    userStateInfo,
    isNavigatingToComplete,
    steps,
    isChangingPackage,
    isDialogOpen,
  ]);

  // Reset cardAdded state when step changes
  useEffect(() => {
    setCardAdded(false);
    setNewlyAddedCard(null);

    // Reset navigation flag when leaving complete step
    if (currentStep !== "complete") {
      setIsNavigatingToComplete(false);
    }

    // Reset package change flag when step changes
    if (currentStep !== "payment_card" && currentStep !== "reservation") {
      setIsChangingPackage(false);
    }
  }, [currentStep]);

  // Fetch numbers when component mounts
  useEffect(() => {
    if (currentStep === "package_selection") {
      fetchNumbers();
    }
  }, [currentStep]);

  // Auto-proceed if user already has payment methods
  useEffect(() => {
    if (currentStep === "payment_card" && cards.length > 0 && !cardAdded) {
      // User already has payment methods, can proceed
      // Don't auto-proceed, let user see their cards and manually proceed
    }
  }, [currentStep, cards.length, cardAdded]);

  const fetchNumbers = async () => {
    try {
      const regularResponse = await authApi.getAvailablePhoneNumbers({
        phoneNumberType: "regular",
        quantity: 27,
      });
      setRegularNumbers(regularResponse.data.phone_numbers || []);

      const tollFreeResponse = await authApi.getAvailablePhoneNumbers({
        phoneNumberType: "toll_free",
        quantity: 27,
      });
      setTollFreeNumbers(tollFreeResponse.data.phone_numbers || []);
    } catch (error) {
      console.error("Error fetching numbers:", error);
      toast.error("Failed to fetch available numbers");
    }
  };

  const fetchPackages = useCallback(
    async (phoneNumber?: string) => {
      // Prevent multiple API calls
      if (isLoadingPackages) return;

      console.log(
        "🔄 Fetching packages for type:",
        selectedType,
        "phoneNumber:",
        phoneNumber
      );
      setIsLoadingPackages(true);

      try {
        // If a specific phone number is provided, fetch packages for that number's type
        if (phoneNumber) {
          const packageType = getPackageTypeFromPhoneNumber(phoneNumber);
          const response = await authApi.getPackages(packageType);
          if (packageType === "regular") {
            setRegularPackages(response.data.packages || []);
            if (response.data.packages && response.data.packages.length > 0) {
              setSelectedPackage(response.data.packages[0]);
              setSelectedPackageIndex(0);
            }
          } else {
            setTollFreePackages(response.data.packages || []);
            if (response.data.packages && response.data.packages.length > 0) {
              setSelectedPackage(response.data.packages[0]);
              setSelectedPackageIndex(0);
            }
          }
          return;
        }

        // Fetch packages for both types when no specific phone number is provided
        const regularPackages = await authApi.getPackages("regular");
        setRegularPackages(regularPackages.data.packages || []);
        if (
          regularPackages.data.packages &&
          regularPackages.data.packages.length > 0
        ) {
          setSelectedPackage(regularPackages.data.packages[0]);
          setSelectedPackageIndex(0);
        }

        const tollFreePackages = await authApi.getPackages("toll_free");
        setTollFreePackages(tollFreePackages.data.packages || []);
        if (
          tollFreePackages.data.packages &&
          tollFreePackages.data.packages.length > 0
        ) {
          setSelectedPackage(tollFreePackages.data.packages[0]);
          setSelectedPackageIndex(0);
        }
      } catch (error) {
        console.error("Error fetching packages:", error);
        toast.error("Failed to fetch packages");
      } finally {
        setIsLoadingPackages(false);
      }
    },
    [isLoadingPackages]
  );

  // Fetch packages when type changes
  useEffect(() => {
    if (currentStep === "package_selection" && !isLoadingPackages) {
      // Only fetch packages if they haven't been loaded yet for the current type
      const currentPackages =
        selectedType === "regular" ? regularPackages : tollFreePackages;
      if (currentPackages.length === 0) {
        console.log(
          "📋 useEffect triggered - fetching packages for type:",
          selectedType
        );
        fetchPackages();
      } else {
        console.log(
          "📋 useEffect triggered - packages already loaded for type:",
          selectedType
        );
      }
    }
  }, [selectedType, currentStep, fetchPackages, isLoadingPackages]);

  const handlePersonalDataComplete = async () => {
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    if (!agreeToTerms) {
      toast.error("Please agree to the terms and conditions");
      return;
    }

    // Store data temporarily without API call
    setTempPersonalData({
      firstName,
      lastName,
      businessName,
      city,
      state,
      line1,
      zipCode,
      country,
      phone,
      businessEmail,
    });

    console.log("Storing personal data locally, going to next step");
    toast.success("Personal data validated successfully!");
    goToNextStep();
  };

  // Handle package selection - Store data locally without API call
  const handlePackageSelect = async () => {
    try {
      console.log("🔵 handlePackageSelect called");
      const currentPackages =
        selectedType === "regular" ? regularPackages : tollFreePackages;

      console.log("📦 Package selection data:", {
        selectedNumber,
        selectedType,
        selectedPackageIndex,
        packagesLength: currentPackages.length,
        selectedPackage: currentPackages[selectedPackageIndex],
      });

      if (!selectedNumber || currentPackages.length === 0) {
        console.log("❌ Validation failed:", {
          hasNumber: !!selectedNumber,
          hasPackages: currentPackages.length > 0,
        });
        toast.error("Please select a number and package");
        return;
      }

      // Store package data temporarily without API call
      console.log("✅ Validation passed, storing data locally...");
      setTempPackageData({
        selectedType,
        selectedNumber,
        selectedPackage: currentPackages[selectedPackageIndex],
        selectedPackageIndex,
      });

      // DON'T update userStateInfo here - it causes useEffect to trigger and causes re-render
      // We only need to store data locally and move to next step
      // The actual state update will happen when payment is completed in handleCompleteAllAPIs

      console.log("➡️ Going to next step...");
      toast.success("Package selected successfully!");
      goToNextStep();
    } catch (error) {
      console.error("❌ Error in handlePackageSelect:", error);
      toast.error("Failed to select package");
    }
  };

  // Handle reservation payment
  const handleReservationPayment = async (
    e?: React.MouseEvent<HTMLButtonElement>
  ) => {
    // Prevent default form submission behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!userStateInfo.has_reserved?.reservation_id) {
      toast.error("No reservation found");
      return;
    }

    setIsLoading(true);
    try {
      const subscriptionResponse = await billingApi.pay(
        userStateInfo.has_reserved.reservation_id
      );
      console.log("✅ Payment API Response:", subscriptionResponse);

      // Check if response has the expected structure
      if (
        !subscriptionResponse.detail ||
        !subscriptionResponse.detail.charges
      ) {
        console.error(
          "❌ Unexpected API response structure:",
          subscriptionResponse
        );
        toast.error("Received unexpected response from payment server");
        setPaymentResultData({
          detail: {
            msg: "Payment response format error",
            charges: [],
            error: "Invalid response structure from payment server",
          },
        });
        return;
      }

      // Check if any charges failed
      const hasFailedCharges = subscriptionResponse.detail.charges.some(
        (charge: any) => charge.status === "failed"
      );

      // Store package information before clearing reservation
      setCompletedPackageInfo({
        package: userStateInfo.has_reserved.package,
        phone_number: userStateInfo.has_reserved.phone_number,
        reservation_id: userStateInfo.has_reserved.reservation_id,
      });

      // Show payment result modal using actual API response data with enhanced package info
      setPaymentResultData({
        detail: {
          msg: subscriptionResponse.detail.msg || "Payment processed.",
          charges: subscriptionResponse.detail.charges.map((charge) => ({
            ...charge,
            // Ensure we have the required fields for the PaymentResult component
            payment_method: {
              ...charge.payment_method,
              card: charge.payment_method?.card
                ? {
                    last4: charge.payment_method.card.last4 || "****",
                    brand: charge.payment_method.card.brand || "Unknown",
                  }
                : null,
            },
          })),
        },
      });

      if (hasFailedCharges) {
        // Show error message for failed payments
        toast.error(
          "Some payment charges failed. Please check the details below."
        );
        console.log(
          "❌ Payment has failed charges:",
          subscriptionResponse.detail.charges.filter(
            (c: any) => c.status === "failed"
          )
        );
      } else {
        // Show success message for successful payments
        toast.success("Payment successful!");
        console.log("🎯 Navigating to complete step after successful payment");
        setIsNavigatingToComplete(true);
        setCurrentStep("complete");

        // Update user state to indicate payment is completed
        const updatedState = {
          ...userStateInfo,
          has_reserved: null, // Clear reservation after successful payment
          package_selection_needed: false, // User has completed the setup
        };
        onUserStateUpdate(updatedState);
        // Don't update static state - keep steps fixed
      }

      // if (subscriptionResponse.data.url) {
      //   window.location.href = subscriptionResponse.data.url;
      // }
    } catch (error: any) {
      console.error("❌ Error processing payment:", error);

      // Check if it's a 402 response (payment failed)
      if (error.response?.status === 402) {
        toast.error(
          "Payment failed. Please check your payment method and try again."
        );

        // Set error data to display in the UI
        setPaymentResultData({
          detail: {
            msg: "Payment failed. Please review the error details below.",
            charges: [],
            error: error.response?.data || "Payment processing failed",
          },
        });
      } else {
        toast.error("Failed to process payment");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle payment retry
  const handlePaymentRetry = async () => {
    if (!userStateInfo.has_reserved?.reservation_id) {
      toast.error("No reservation found");
      return;
    }

    // Clear previous payment result data
    setPaymentResultData(null);

    // Try payment again
    await handleReservationPayment();
  };

  // New function to handle all API calls in sequence during payment step
  const handleCompleteAllAPIs = async () => {
    setIsLoading(true);
    try {
      console.log("🚀 Starting complete data submission process...");
      console.log("📦 Data to be sent:", {
        hasPersonalData: !!tempPersonalData,
        hasPackageData: !!tempPackageData,
        hasReservation: !!userStateInfo.has_reserved,
      });

      // Step 1: Complete Personal Data API (if we have temp data)
      if (tempPersonalData) {
        console.log("🔄 Step 1: Submitting personal data...");
        console.log("👤 Personal Data:", {
          firstName: tempPersonalData.firstName,
          lastName: tempPersonalData.lastName,
          businessName: tempPersonalData.businessName,
          email: tempPersonalData.businessEmail,
          phone: tempPersonalData.phone,
          address: {
            city: tempPersonalData.city,
            state: tempPersonalData.state,
            line1: tempPersonalData.line1,
            zipCode: tempPersonalData.zipCode,
            country: tempPersonalData.country,
          },
        });

        await authApi.completePersonalData(
          tempPersonalData.firstName,
          tempPersonalData.lastName
        );
        console.log("✅ Personal data API call successful");

        // Format phone number for API call
        const formattedPhone = tempPersonalData.phone.startsWith("+1")
          ? tempPersonalData.phone.replace("+", "")
          : tempPersonalData.phone.replace(/^\+\d{1,3}/, "");

        await authApi.completeBusinessData(
          tempPersonalData.businessName,
          [tempPersonalData.businessEmail],
          [formattedPhone],
          {
            city: tempPersonalData.city,
            state: tempPersonalData.state,
            line_1: tempPersonalData.line1,
            zip_code: tempPersonalData.zipCode,
            country: tempPersonalData.country,
          }
        );
        console.log("✅ Business data API call successful");
        console.log(
          "✅ Step 1: Personal and business data submitted successfully"
        );
        toast.success("Personal data submitted successfully");
      } else {
        console.log("⚠️ No personal data to submit (already submitted)");
      }

      // Step 2: Create Reservation API (if we have temp package data)
      let currentReservationId = userStateInfo.has_reserved?.reservation_id;

      if (tempPackageData) {
        console.log("🔄 Step 2: Creating reservation...");
        console.log("📞 Package & Phone Data:", {
          packageId: tempPackageData.selectedPackage.id,
          packageName: tempPackageData.selectedPackage.name,
          phoneNumber: tempPackageData.selectedNumber,
          type: tempPackageData.selectedType,
        });

        const formattedPhoneNumber = ensurePhoneNumberFormat(
          tempPackageData.selectedNumber
        );
        console.log("📞 Formatted phone number:", formattedPhoneNumber);

        const { packagesApi } = await import("../../../api/packagesApi");
        const reservationResponse = await packagesApi.createReservation(
          tempPackageData.selectedPackage.id,
          formattedPhoneNumber
        );
        console.log(
          "✅ Step 2: Reservation created successfully",
          reservationResponse.data
        );
        toast.success("Reservation created successfully");

        currentReservationId = reservationResponse.data.reservation_id;
        console.log("🎫 Reservation ID:", currentReservationId);

        // DON'T update userStateInfo here - it causes useEffect to trigger
        // We're in the middle of API calls sequence, no need to update state
        // The final result will be shown in "complete" step
      } else {
        console.log(
          "⚠️ No package data to submit, using existing reservation:",
          currentReservationId
        );
      }

      // Step 3: Process payment with the reservation
      if (currentReservationId) {
        console.log(
          "🔄 Step 3: Processing payment for reservation:",
          currentReservationId
        );
        console.log("💳 Initiating payment API call...");

        const subscriptionResponse = await billingApi.pay(currentReservationId);
        console.log(
          "✅ Step 3: Payment processed successfully",
          subscriptionResponse
        );
        console.log("💰 Payment response details:", {
          hasCharges: !!subscriptionResponse.detail?.charges,
          chargesCount: subscriptionResponse.detail?.charges?.length || 0,
          message: subscriptionResponse.detail?.msg,
        });

        // Check if payment was successful
        if (
          subscriptionResponse.detail &&
          subscriptionResponse.detail.charges
        ) {
          const hasFailedCharges = subscriptionResponse.detail.charges.some(
            (charge: any) => charge.status === "failed"
          );

          console.log(
            "💳 Payment status:",
            hasFailedCharges ? "FAILED" : "SUCCESS"
          );

          if (hasFailedCharges) {
            console.error("❌ Some charges failed");
            toast.error("Payment failed. Please try again.");
            setPaymentResultData(subscriptionResponse);
            setCurrentStep("complete");
          } else {
            console.log("✅ All charges successful");
            toast.success("Payment completed successfully!");
            setPaymentResultData(subscriptionResponse);
            setIsNavigatingToComplete(true);
            setCurrentStep("complete");
          }
        } else {
          console.log("✅ Payment completed (no charge details)");
          toast.success("Setup completed successfully!");
          setIsNavigatingToComplete(true);
          setCurrentStep("complete");
        }

        console.log("🎉 All data submitted successfully to backend!");
        console.log("📋 Summary:");
        console.log("  ✅ Personal Data: Submitted");
        console.log("  ✅ Business Data: Submitted");
        console.log("  ✅ Reservation: Created");
        console.log("  ✅ Payment: Processed");
      } else {
        console.error("❌ No reservation ID found, cannot process payment");
        toast.error("No reservation found to process payment");
      }
    } catch (error: any) {
      console.error("❌ Error during API submission:", error);
      console.error("❌ Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      const errorMessage = error.message || "Failed to complete setup";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
      console.log("🏁 Data submission process completed");
    }
  };

  // Handle payment card setup
  const handlePaymentCardComplete = async () => {
    try {
      // Refetch cards to get the latest data
      await refetchCards();

      // Show success message
      const successMessage = isReplacingCard
        ? "🎉 Payment method updated successfully! Your new card is now set as default and old cards have been removed."
        : `✅ Payment method added successfully! You can now complete your setup.`;

      toast.success(successMessage, { duration: 4000 });

      setCardAdded(true);
      setIsReplacingCard(false);
      setIsReplacingCardLoading(false);
      setCurrentCardId(undefined); // Reset the current card ID after completion
      setShowReplaceConfirmation(false); // Reset replace confirmation dialog
      setShowAddCard(false); // Ensure add card dialog is closed

      // Final cache invalidation to ensure UI is fully updated
      await queryClient.invalidateQueries({ queryKey: ["paymentMethods"] });
      console.log("✅ Final cache invalidation completed");
      console.log("✅ All states reset for next card operation");

      // DON'T automatically call handleCompleteAllAPIs here
      // Let the user see the added card and click "Complete Setup" button manually
      console.log(
        "✅ Card added successfully. User can now click Complete Setup button."
      );
    } catch (error) {
      console.error("Error handling payment card completion:", error);
      toast.error("Failed to refresh payment methods. Please try again.");
    } finally {
      // Reset dialog flag AFTER all operations are complete
      setIsDialogOpen(false);
    }
  };

  // Handle card replacement
  const handleReplaceCard = (e?: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent default form submission behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    console.log("🔍 DEBUG - handleReplaceCard called");
    console.log("🔍 DEBUG - Current cards:", cards);
    console.log("🔍 DEBUG - Cards length:", cards.length);
    console.log("🔍 DEBUG - showAddCard state:", showAddCard);
    console.log(
      "🔍 DEBUG - showReplaceConfirmation state:",
      showReplaceConfirmation
    );

    // Set dialog open flag to prevent step reset
    setIsDialogOpen(true);

    if (cards.length === 0) {
      console.log(
        "🔍 DEBUG - No cards found, opening add card dialog directly"
      );
      // Use setTimeout to ensure state updates after event handling is complete
      setTimeout(() => {
        setShowAddCard(true);
        console.log("🔍 DEBUG - setShowAddCard(true) called");
      }, 0);
      return;
    }

    // Get the current default card ID
    const defaultCard = cards.find((card: any) => card.is_default) || cards[0];
    const cardId = defaultCard?.id;

    console.log("🔍 DEBUG - Default card found:", defaultCard);
    console.log("🔍 DEBUG - Card ID to replace:", cardId);

    // Store the current card ID for replacement
    setCurrentCardId(cardId);

    // Use setTimeout to ensure state updates after event handling is complete
    // This prevents race condition bugs where dialog state isn't set properly
    setTimeout(() => {
      // If dialog is already open, close and reopen it for visual feedback
      if (showReplaceConfirmation) {
        console.log("🔍 DEBUG - Dialog already open, refreshing it");
        setShowReplaceConfirmation(false);
        // Use a short timeout to allow the close animation and then reopen
        setTimeout(() => {
          setShowReplaceConfirmation(true);
          console.log("🔍 DEBUG - Dialog reopened");
        }, 50);
      } else {
        // Show confirmation dialog for the first time
        console.log("🔍 DEBUG - Setting showReplaceConfirmation to true");
        setShowReplaceConfirmation(true);
      }
    }, 0);
  };

  // Confirm card replacement
  const confirmCardReplacement = () => {
    console.log("🔍 DEBUG - confirmCardReplacement called");
    console.log("🔍 DEBUG - Current state before confirmation:");
    console.log("  - showReplaceConfirmation:", showReplaceConfirmation);
    console.log("  - isReplacingCard:", isReplacingCard);
    console.log("  - showAddCard:", showAddCard);
    console.log("  - isDialogOpen:", isDialogOpen);
    console.log("  - cardAdded:", cardAdded);

    console.log("🔍 DEBUG - Resetting states for card replacement");
    setShowReplaceConfirmation(false);
    setIsReplacingCard(true);
    setCardAdded(false); // Reset cardAdded flag to allow new card UI flow
    setShowAddCard(true);

    // Show informative message about the replacement process
    toast(
      "Adding new card... Your new card will be set as default and old cards will be removed.",
      {
        duration: 4000,
      }
    );
    console.log("🔍 DEBUG - States updated, dialog should open now");
  };

  // Cancel card replacement
  const cancelCardReplacement = () => {
    setShowReplaceConfirmation(false);
    setIsReplacingCard(false);
    setIsReplacingCardLoading(false);
    setCurrentCardId(undefined); // Reset the current card ID
    setIsDialogOpen(false); // Reset dialog flag
    toast("Card replacement cancelled", { duration: 3000 });
  };

  // Enhanced card replacement handler that sets new card as default and deletes old one
  const handleEnhancedCardReplacement = async (
    newCardId: string,
    oldCardId?: string
  ) => {
    setIsReplacingCardLoading(true);

    try {
      // Step 1: Get current cards and delete the old card if specified, otherwise delete all existing cards
      console.log("🔄 Step 1: Deleting old card(s)...");
      console.log("🔍 DEBUG - oldCardId received:", oldCardId);
      console.log("🔍 DEBUG - newCardId received:", newCardId);

      await queryClient.invalidateQueries({ queryKey: ["paymentMethods"] });
      const currentCardsResult = await refetchCards();
      const currentCards = currentCardsResult.data || [];

      console.log("🔍 DEBUG - Current cards before deletion:", currentCards);
      console.log("🔍 DEBUG - Number of current cards:", currentCards.length);

      if (currentCards.length > 0) {
        if (oldCardId) {
          // Delete only the specific old card
          console.log("🔍 DEBUG - Looking for oldCardId:", oldCardId);
          const oldCard = currentCards.find(
            (card: any) => card.id === oldCardId
          );
          console.log("🔍 DEBUG - Found old card:", oldCard);
          if (oldCard) {
            console.log(
              `🗑️ Deleting specific old card ${oldCard.id} (${oldCard.card?.last4})...`
            );
            try {
              await new Promise<void>((resolve, reject) => {
                deletePaymentMethod(oldCard.id, {
                  onSuccess: () => {
                    console.log(
                      `✅ Old card ${oldCard.id} (${oldCard.card?.last4}) deleted successfully`
                    );
                    resolve();
                  },
                  onError: (error) => {
                    console.error(
                      `❌ Failed to delete old card ${oldCard.id}:`,
                      error
                    );
                    reject(
                      new Error(`Failed to delete old card ${oldCard.id}`)
                    );
                  },
                });
              });
            } catch (deleteError) {
              console.error(
                `❌ Error deleting old card ${oldCard.id}:`,
                deleteError
              );
              // Silently continue - don't show error to user
            }
          } else {
            console.log(
              "🔍 DEBUG - oldCardId not found in current cards, falling back to delete all"
            );
          }
        } else {
          // Fallback: Delete ALL existing cards (original behavior)
          console.log(
            `🗑️ No oldCardId specified, deleting ALL ${currentCards.length} existing cards...`
          );
          for (const existingCard of currentCards) {
            try {
              console.log(
                `🗑️ Deleting existing card ${existingCard.id} (${existingCard.card?.last4})...`
              );
              await new Promise<void>((resolve, reject) => {
                deletePaymentMethod(existingCard.id, {
                  onSuccess: () => {
                    console.log(
                      `✅ Existing card ${existingCard.id} (${existingCard.card?.last4}) deleted successfully`
                    );
                    resolve();
                  },
                  onError: (error) => {
                    console.error(
                      `❌ Failed to delete existing card ${existingCard.id}:`,
                      error
                    );
                    reject(
                      new Error(
                        `Failed to delete existing card ${existingCard.id}`
                      )
                    );
                  },
                });
              });
            } catch (deleteError) {
              console.error(
                `❌ Error deleting existing card ${existingCard.id}:`,
                deleteError
              );
              // Silently continue - don't show error to user
            }
          }
        }

        // Wait a bit for deletions to complete
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      // Step 2: Set the new card as default payment method
      console.log("🔄 Step 2: Setting new card as default...");
      console.log("🔍 DEBUG - Setting newCardId as default:", newCardId);

      try {
        await new Promise<void>((resolve, reject) => {
          setDefaultPaymentMethod(newCardId, {
            onSuccess: () => {
              console.log("✅ New card set as default successfully");
              resolve();
            },
            onError: (error) => {
              console.error("❌ Failed to set new card as default:", error);
              reject(new Error("Failed to set new card as default"));
            },
          });
        });

        toast.success("New card set as default payment method");
      } catch (error) {
        console.error("❌ Error setting new card as default:", error);
        toast.error("Failed to set new card as default. Please try again.");
        throw error; // Re-throw to trigger error handling
      }

      // Step 3: Verify only one card exists and get its details
      console.log("🔄 Step 3: Verifying only one card exists...");
      await queryClient.invalidateQueries({ queryKey: ["paymentMethods"] });
      const finalResult = await refetchCards();
      const finalCards = finalResult.data || [];
      console.log("📋 Final cards after replacement:", finalCards);

      // Verify only one card exists
      if (finalCards.length === 1) {
        console.log("✅ Success: Only one card remains in the system");
        const singleCard = finalCards[0];

        // Step 4: Update the newly added card state
        console.log("🔄 Step 4: Updating card state...");
        setNewlyAddedCard(singleCard);
        console.log("✅ New card state updated successfully:", singleCard);

        // Step 5: Update user state to reflect the single card
        console.log("🔄 Step 5: Updating user state...");
        const updatedState = {
          ...userStateInfo,
          has_payment_card: {
            payment_methods: [singleCard],
          },
        };
        onUserStateUpdate(updatedState);
        // Don't update static state - keep steps fixed
        console.log("✅ User state updated successfully");

        toast.success("Card replacement completed successfully!");
      } else if (finalCards.length === 0) {
        console.error("❌ Error: No cards found after replacement");
        toast.error(
          "Error: No payment method found after replacement. Please try again."
        );
      } else {
        console.error(
          `❌ Error: ${finalCards.length} cards found after replacement - should be exactly 1`
        );
        toast.error(
          `Error: Found ${finalCards.length} payment methods after replacement. Please contact support.`
        );

        // Force delete all cards except the new one
        const cardsToDelete = finalCards.filter(
          (card: any) => card.id !== newCardId
        );
        console.log(`🔄 Force deleting ${cardsToDelete.length} extra cards...`);

        for (const extraCard of cardsToDelete) {
          try {
            await new Promise<void>((resolve, reject) => {
              deletePaymentMethod(extraCard.id, {
                onSuccess: () => {
                  console.log(
                    `✅ Extra card ${extraCard.id} deleted successfully`
                  );
                  resolve();
                },
                onError: (error) => {
                  console.error(
                    `❌ Failed to delete extra card ${extraCard.id}:`,
                    error
                  );
                  reject(error);
                },
              });
            });
          } catch (error) {
            console.error(
              `❌ Error deleting extra card ${extraCard.id}:`,
              error
            );
          }
        }
      }

      // Final cache invalidation to ensure UI is fully updated
      await queryClient.invalidateQueries({ queryKey: ["paymentMethods"] });
      console.log("✅ Final cache invalidation completed");
    } catch (error: any) {
      console.error("❌ Error during card replacement:", error);

      // Provide specific error messages based on the failure point
      let errorMessage = "Failed to complete card replacement";
      if (error.message.includes("set new card as default")) {
        errorMessage = "Failed to set new card as default. Please try again.";
      } else if (error.message.includes("delete existing card")) {
        errorMessage =
          "Failed to remove existing cards. Please contact support.";
      }

      toast.error(errorMessage);

      // Even if some steps failed, try to update the UI with what we have
      try {
        const result = await refetchCards();
        const updatedCards = result.data || [];
        if (updatedCards.length === 1) {
          const singleCard = updatedCards[0];
          setNewlyAddedCard(singleCard);
          const updatedState = {
            ...userStateInfo,
            has_payment_card: {
              payment_methods: [singleCard],
            },
          };
          onUserStateUpdate(updatedState);
          // Don't update static state - keep steps fixed
        }
      } catch (refreshError) {
        console.error(
          "Failed to refresh payment methods after error:",
          refreshError
        );
      }
    } finally {
      setIsReplacingCardLoading(false);
    }
  };

  const goToNextStep = () => {
    const currentIndex = steps.findIndex((step) => step.id === currentStep);
    console.log(
      "goToNextStep - currentIndex:",
      currentIndex,
      "steps.length:",
      steps.length
    );
    console.log(
      "Current step:",
      currentStep,
      "Next step:",
      steps[currentIndex + 1]?.id
    );

    if (currentIndex < steps.length - 1) {
      console.log("Moving to next step:", steps[currentIndex + 1].id);
      setCurrentStep(steps[currentIndex + 1].id);
    } else {
      console.log("No more steps, completing");
      onComplete();
    }
  };

  const getCurrentStepIndex = () => {
    if (steps.length === 0) return 0;
    return steps.findIndex((step) => step.id === currentStep);
  };

  const getProgressPercentage = () => {
    if (steps.length === 0) return 0;
    const currentIndex = getCurrentStepIndex();
    if (currentIndex === -1) return 0;
    return ((currentIndex + 1) / steps.length) * 100;
  };

  // Handle previous/next navigation
  const handlePrevious = (e?: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent default form submission behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const currentIndex = getCurrentStepIndex();
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1].id);
    }
  };

  const handleNext = (e?: React.MouseEvent<HTMLButtonElement>) => {
    console.log("🔵 handleNext called, currentStep:", currentStep);

    // Prevent default form submission behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const currentIndex = getCurrentStepIndex();
    console.log(
      "📍 Current step index:",
      currentIndex,
      "Total steps:",
      steps.length
    );

    if (currentIndex < steps.length - 1) {
      // Trigger the appropriate action based on current step
      if (currentStep === "personal_data") {
        console.log("➡️ Calling handlePersonalDataComplete...");
        handlePersonalDataComplete();
      } else if (currentStep === "package_selection") {
        console.log("➡️ Calling handlePackageSelect...");
        handlePackageSelect();
      } else {
        console.log("➡️ Calling goToNextStep...");
        goToNextStep();
      }
    } else {
      console.log("⚠️ Already at last step");
    }
  };

  const canGoNext = () => {
    if (currentStep === "personal_data") {
      return (
        !isLoading &&
        firstName &&
        lastName &&
        agreeToTerms &&
        businessName &&
        city &&
        state &&
        line1 &&
        zipCode &&
        country &&
        validatePhone(phone).isValid &&
        businessEmail
      );
    } else if (currentStep === "package_selection") {
      const currentPackages =
        selectedType === "regular" ? regularPackages : tollFreePackages;
      const isValid =
        !isLoading && selectedNumber && currentPackages.length > 0;

      console.log("canGoNext for package_selection:", {
        isLoading,
        selectedNumber,
        selectedType,
        packagesLength: currentPackages.length,
        isValid,
      });

      return isValid;
    } else if (currentStep === "payment_card") {
      return !isLoading && cards.length > 0;
    }
    return false;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "personal_data":
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    clearFieldError("firstName");
                  }}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    validationErrors.firstName
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:ring-green-500"
                  }`}
                  placeholder="First name *"
                />
                {showValidationErrors && validationErrors.firstName && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.firstName}
                  </p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    clearFieldError("lastName");
                  }}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    validationErrors.lastName
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:ring-green-500"
                  }`}
                  placeholder="Last name *"
                />
                {showValidationErrors && validationErrors.lastName && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.lastName}
                  </p>
                )}
              </div>
            </div>

            {/* Business Name */}
            <div>
              <input
                type="text"
                value={businessName}
                onChange={(e) => {
                  setBusinessName(e.target.value);
                  clearFieldError("businessName");
                }}
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                  validationErrors.businessName
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-200 focus:ring-green-500"
                }`}
                placeholder="Company Name *"
              />
              {showValidationErrors && validationErrors.businessName && (
                <p className="text-red-500 text-xs mt-1">
                  {validationErrors.businessName}
                </p>
              )}
            </div>

            {/* Contact Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <PhoneInput
                  value={phone}
                  onChange={(value) => {
                    setPhone(value);
                    clearFieldError("phone");
                  }}
                  defaultCountry="us"
                  className={`${
                    validationErrors.phone
                      ? "border-red-500 focus-within:ring-red-500"
                      : "border-gray-200 focus-within:ring-green-500"
                  }`}
                  inputStyle={{
                    width: "100%",
                    padding: "12px",
                    height: "48px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "0 8px 8px 0",
                    outline: "none",
                    fontSize: "16px",
                  }}
                  countrySelectorStyleProps={{
                    buttonStyle: {
                      padding: "12px",
                      height: "48px",
                      border: "1px solid #e5e7eb",
                      borderRight: "none",
                      borderRadius: "8px 0 0 8px",
                      backgroundColor: "#f9fafb",
                    },
                  }}
                />
                {showValidationErrors && validationErrors.phone && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.phone}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={businessEmail}
                  onChange={(e) => {
                    setBusinessEmail(e.target.value);
                    clearFieldError("businessEmail");
                  }}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    validationErrors.businessEmail
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:ring-green-500"
                  }`}
                  placeholder="your@email.com"
                />
                {showValidationErrors && validationErrors.businessEmail && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.businessEmail}
                  </p>
                )}
              </div>
            </div>

            {/* Address Input Section */}
            <div>
              <div className="mb-3">
                <label className="text-sm font-medium text-gray-700">
                  Address <span className="text-red-500">*</span>
                </label>
              </div>

              {/* Google Maps Autocomplete */}
              <AddressAutocomplete
                value={fullAddress}
                onChange={setFullAddress}
                onAddressSelect={handleAddressSelect}
                placeholder="Search for your address using Google Maps"
                className={`w-full ${
                  validationErrors.line1 ? "border-red-500" : ""
                }`}
              />

              {showValidationErrors && validationErrors.line1 && (
                <p className="text-red-500 text-xs mt-1">
                  {validationErrors.line1}
                </p>
              )}
            </div>

            {/* Address Fields - Auto-filled by autocomplete but editable */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    clearFieldError("city");
                  }}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    validationErrors.city
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:ring-green-500"
                  }`}
                  placeholder="City *"
                  title="Auto-filled from address selection, but you can edit manually"
                />
                {showValidationErrors && validationErrors.city && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.city}
                  </p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => {
                    setState(e.target.value);
                    clearFieldError("state");
                  }}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    validationErrors.state
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:ring-green-500"
                  }`}
                  placeholder="State *"
                  title="Auto-filled from address selection, but you can edit manually"
                />
                {showValidationErrors && validationErrors.state && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.state}
                  </p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value);
                  }}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 border-gray-200 focus:ring-green-500"
                  placeholder="Country *"
                  title="Country (defaults to USA)"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => {
                    setZipCode(e.target.value);
                    clearFieldError("zipCode");
                  }}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    validationErrors.zipCode
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:ring-green-500"
                  }`}
                  placeholder="Zip Code *"
                  title="Auto-filled from address selection, but you can edit manually"
                />
                {showValidationErrors && validationErrors.zipCode && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.zipCode}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="terms"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-green-500 focus:ring-green-500"
              />
              <label htmlFor="terms" className="text-sm text-gray-600">
                I agree to the Terms of service and Privacy policies{" "}
                <span className="text-red-500">*</span>
              </label>
            </div>
          </div>
        );

      case "package_selection":
        return (
          <div className="grid grid-cols-2 gap-4 h-full">
            {/* Left Panel - Phone Number Selection */}
            <div className="space-y-3 bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-4 border border-blue-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <span className="w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md">
                    1
                  </span>
                  Phone Number
                </h3>

                {/* Number Type Selection - Compact */}
                <div className="bg-white p-0.5 rounded-lg flex gap-0.5 shadow-sm border border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedType("regular");
                      setSelectedNumber(null);
                      setSelectedPackageIndex(0);
                      setSelectedPackage(null);
                    }}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${
                      selectedType === "regular"
                        ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm"
                        : "bg-transparent text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    📞 Local
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedType("toll_free");
                      setSelectedNumber(null);
                      setSelectedPackageIndex(0);
                      setSelectedPackage(null);
                    }}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${
                      selectedType === "toll_free"
                        ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-sm"
                        : "bg-transparent text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    🌟 Toll Free
                  </button>
                </div>
              </div>

              {/* Number Grid */}
              <div className="grid grid-cols-2 gap-2 max-h-[520px] overflow-y-auto p-2 bg-white rounded-lg border border-gray-200">
                {selectedType === "regular" ? (
                  <>
                    {/* Test Regular Numbers */}
                    {testRegularNumbers.map((phone, index) => (
                      <div
                        key={`test-regular-${index}`}
                        onClick={() => {
                          const phoneNumber = ensurePhoneNumberFormat(
                            phone.full_number
                          );
                          setSelectedNumber(phoneNumber);
                          setSelectedPackageIndex(0);
                          setSelectedPackage(null);
                          fetchPackages(phoneNumber);
                        }}
                        className={`flex border-2 border-dashed rounded-lg hover:shadow-md hover:scale-[1.02] transition-all duration-200 cursor-pointer ${
                          selectedNumber ===
                          ensurePhoneNumberFormat(phone.full_number)
                            ? "bg-gradient-to-br from-orange-50 to-orange-100 border-orange-400 shadow-sm ring-2 ring-orange-200"
                            : "bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-300 hover:border-orange-300"
                        }`}
                      >
                        <div className="w-1 h-full rounded-l-lg">
                          <div className="w-1 h-full rounded-l-lg bg-gradient-to-b from-orange-400 to-orange-600"></div>
                        </div>
                        <div className="flex flex-col gap-1 p-2.5 w-full">
                          <div className="flex items-center gap-1.5">
                            <p
                              className={`text-xs font-bold ${
                                selectedNumber ===
                                ensurePhoneNumberFormat(phone.full_number)
                                  ? "text-orange-900"
                                  : "text-orange-800"
                              }`}
                            >
                              {formatPhoneNumberForDisplay(phone.full_number)}
                            </p>
                            <span className="text-[10px] bg-orange-200 text-orange-800 px-1.5 py-0.5 rounded-full font-semibold">
                              TEST
                            </span>
                          </div>
                          <p
                            className={`text-[10px] font-medium ${
                              selectedNumber ===
                              ensurePhoneNumberFormat(phone.full_number)
                                ? "text-orange-700"
                                : "text-orange-600"
                            }`}
                          >
                            📍 {phone.city}, {phone.state}
                          </p>
                          <div className="flex justify-between items-center mt-0.5">
                            <p
                              className={`text-[10px] ${
                                selectedNumber ===
                                ensurePhoneNumberFormat(phone.full_number)
                                  ? "text-orange-600"
                                  : "text-orange-500"
                              }`}
                            >
                              {phone.rate_center}
                            </p>
                            <FiInfo
                              className={`w-3 h-3 ${
                                selectedNumber ===
                                ensurePhoneNumberFormat(phone.full_number)
                                  ? "text-orange-600"
                                  : "text-orange-500"
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Real Regular Numbers */}
                    {regularNumbers.map((phone, index) => {
                      const phoneNumber = phone.full_number.replace(/^\+1/, "");
                      return (
                        <div
                          key={index}
                          onClick={() => {
                            setSelectedNumber(phoneNumber);
                            setSelectedPackageIndex(0);
                            setSelectedPackage(null);
                            fetchPackages(phoneNumber);
                          }}
                          className={`flex border-2 rounded-lg hover:shadow-md hover:scale-[1.02] transition-all duration-200 cursor-pointer ${
                            selectedNumber === phoneNumber
                              ? "bg-gradient-to-br from-blue-50 to-green-50 border-blue-400 shadow-sm ring-2 ring-blue-200"
                              : "bg-white border-gray-200 hover:border-blue-300"
                          }`}
                        >
                          {/* US Flag on the left */}
                          <div className="w-1 h-full rounded-l-lg overflow-hidden">
                            <img
                              src={usFlagImage}
                              alt="US Flag"
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Phone number content */}
                          <div className="flex flex-col gap-1 p-2.5 w-full">
                            <p className="text-xs font-bold text-gray-900">
                              {formatPhoneNumberForDisplay(phoneNumber)}
                            </p>
                            <p className="text-[10px] font-medium text-gray-600">
                              📍 {phone.city}, {phone.state}
                            </p>
                            <div className="flex justify-between items-center mt-0.5">
                              <div className="flex gap-1">
                                <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
                                  Voice
                                </span>
                                <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">
                                  SMS
                                </span>
                              </div>
                              <FiInfo className="w-3 h-3 text-gray-400" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {/* Test Toll-Free Numbers */}
                    {testTollFreeNumbers.map((phone, index) => (
                      <div
                        key={`test-tollfree-${index}`}
                        onClick={() => {
                          const phoneNumber = ensurePhoneNumberFormat(phone);
                          setSelectedNumber(phoneNumber);
                          setSelectedPackageIndex(0);
                          setSelectedPackage(null);
                          fetchPackages(phoneNumber);
                        }}
                        className={`flex border-2 border-dashed rounded-lg hover:shadow-md hover:scale-[1.02] transition-all duration-200 cursor-pointer ${
                          selectedNumber === ensurePhoneNumberFormat(phone)
                            ? "bg-gradient-to-br from-purple-50 to-purple-100 border-purple-400 shadow-sm ring-2 ring-purple-200"
                            : "bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300 hover:border-purple-300"
                        }`}
                      >
                        <div className="w-1 h-full rounded-l-lg">
                          <div className="w-1 h-full rounded-l-lg bg-gradient-to-b from-purple-400 to-purple-600"></div>
                        </div>
                        <div className="flex flex-col gap-1 p-2.5 w-full">
                          <div className="flex items-center gap-1.5">
                            <p
                              className={`text-xs font-bold ${
                                selectedNumber ===
                                ensurePhoneNumberFormat(phone)
                                  ? "text-purple-900"
                                  : "text-purple-800"
                              }`}
                            >
                              {phone}
                            </p>
                            <span className="text-[10px] bg-purple-200 text-purple-800 px-1.5 py-0.5 rounded-full font-semibold">
                              TEST
                            </span>
                          </div>
                          <p
                            className={`text-[10px] font-medium ${
                              selectedNumber === ensurePhoneNumberFormat(phone)
                                ? "text-purple-700"
                                : "text-purple-600"
                            }`}
                          >
                            🌟 Toll Free Number
                          </p>
                          <div className="flex justify-between items-center mt-0.5">
                            <p className="text-[10px] text-purple-500">
                              National Coverage
                            </p>
                            <FiInfo
                              className={`w-3 h-3 ${
                                selectedNumber ===
                                ensurePhoneNumberFormat(phone)
                                  ? "text-purple-600"
                                  : "text-purple-500"
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Real Toll-Free Numbers */}
                    {tollFreeNumbers.map((phone, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          setSelectedNumber(phone);
                          setSelectedPackageIndex(0);
                          setSelectedPackage(null);
                          fetchPackages(phone);
                        }}
                        className={`flex border-2 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer ${
                          selectedNumber === phone
                            ? "bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-400 shadow-md ring-2 ring-purple-300"
                            : "bg-white border-gray-200 hover:border-purple-300"
                        }`}
                      >
                        {/* US Flag on the left */}
                        <div className="w-1 h-full rounded-l-xl overflow-hidden">
                          <img
                            src={usFlagImage}
                            alt="US Flag"
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Phone number content */}
                        <div className="flex flex-col gap-1.5 p-4 w-full">
                          <p className="text-sm font-bold text-gray-900">
                            {formatPhoneNumberForDisplay(phone)}
                          </p>
                          <p className="text-xs font-medium text-purple-600">
                            🌟 Toll Free
                          </p>
                          <div className="flex justify-between items-center mt-1">
                            <div className="flex gap-1">
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                                Nationwide
                              </span>
                            </div>
                            <FiInfo className="w-4 h-4 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* Right Panel - Package Selection */}
            <div className="space-y-3 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-4 border border-green-200 shadow-sm">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <span className="w-7 h-7 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md">
                  2
                </span>
                Package Plan
              </h3>

              {selectedNumber ? (
                <div className="space-y-3">
                  {/* Show loading state while packages are being fetched */}
                  {((selectedType === "regular" &&
                    regularPackages.length === 0) ||
                    (selectedType === "toll_free" &&
                      tollFreePackages.length === 0) ||
                    isLoadingPackages) && (
                    <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
                      <div className="animate-spin rounded-full h-10 w-10 border-3 border-blue-600 border-t-transparent mx-auto mb-3"></div>
                      <p className="text-sm font-medium text-gray-700">
                        Loading packages...
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Please wait</p>
                    </div>
                  )}

                  {/* Show packages when available */}
                  {((selectedType === "regular" &&
                    regularPackages.length > 0) ||
                    (selectedType === "toll_free" &&
                      tollFreePackages.length > 0)) && (
                    <>
                      <div className="grid grid-cols-1 gap-3">
                        {(selectedType === "regular"
                          ? regularPackages
                          : tollFreePackages
                        ).map((pkg: any, index: number) => (
                          <div
                            key={index}
                            onClick={() => {
                              setSelectedPackageIndex(index);
                              setSelectedPackage(pkg);
                            }}
                            className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
                              selectedPackageIndex === index
                                ? "border-green-500 bg-white shadow-md ring-2 ring-green-300"
                                : "border-gray-200 bg-white hover:border-green-300"
                            }`}
                          >
                            {selectedPackageIndex === index && (
                              <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1.5 shadow-md">
                                <FiCheck className="w-4 h-4" />
                              </div>
                            )}
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <h5 className="font-bold text-base text-gray-900 mb-1">
                                  {pkg.name}
                                </h5>
                                <p className="text-xs text-gray-600 leading-relaxed">
                                  {pkg.description}
                                </p>
                              </div>
                              <div className="text-right ml-3">
                                <div className="bg-gradient-to-br from-green-500 to-blue-500 text-white rounded-lg px-3 py-1.5 shadow-sm">
                                  <p className="font-bold text-lg">
                                    ${pkg.price}
                                  </p>
                                  <p className="text-xs opacity-90">/mo</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                              <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                📞 {pkg.minutes} min
                              </span>
                              {pkg.fee_per_overage_minute && (
                                <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                  ${pkg.fee_per_overage_minute}/min
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[400px] text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <span className="text-4xl">📞</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-700 mb-2">
                    Select a Phone Number First
                  </h4>
                  <p className="text-sm text-gray-500 max-w-xs">
                    Choose a phone number from the left panel to view available
                    packages
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case "reservation":
        // Debug reservation step
        console.log("🔍 DEBUG - Rendering reservation step");
        console.log("🔍 DEBUG - Current cards in reservation step:", cards);
        console.log(
          "🔍 DEBUG - Cards length in reservation step:",
          cards.length
        );
        console.log(
          "🔍 DEBUG - has_payment_card:",
          userStateInfo.has_payment_card
        );

        return (
          <div className="space-y-6">
            {/* Side by side layout for reservation details and payment method */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Reservation Details - Left Side */}
              {userStateInfo.has_reserved && (
                <div className="border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <h5 className="font-semibold text-gray-800 text-lg">
                        Reservation Details
                      </h5>
                    </div>
                    <button
                      type="button"
                      onClick={handleShowReservationActions}
                      className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Manage Reservation
                    </button>
                  </div>

                  {/* Package Details */}
                  <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h6 className="font-semibold text-gray-900 text-base">
                          {userStateInfo.has_reserved.package.name}
                        </h6>
                        <p className="text-sm text-gray-600">
                          {userStateInfo.has_reserved?.package.minutes} minutes
                          included
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-xl text-gray-900">
                          ${userStateInfo.has_reserved?.package.price}
                        </p>
                        <p className="text-xs text-gray-500">per month</p>
                      </div>
                    </div>

                    {/* Phone Number */}
                    <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-sm font-medium">
                          📞
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Reserved Phone Number
                        </p>
                        <p className="font-semibold text-gray-900">
                          {formatPhoneNumberForDisplay(
                            userStateInfo.has_reserved.phone_number
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Reservation Info */}
                    <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                        <span className="text-orange-600 text-sm font-medium">
                          ⏰
                        </span>
                      </div>
                      <div className="flex-1">
                        {(() => {
                          const remainingTime = calculateRemainingTime(
                            userStateInfo.has_reserved.expires_at
                          );
                          return (
                            <>
                              {!remainingTime.isExpired && (
                                <div className="mt-2 p-2 bg-orange-50 rounded-lg border border-orange-200">
                                  <p className="text-xs text-orange-700 font-medium mb-1">
                                    Time Remaining:
                                  </p>
                                  <div className="flex items-center gap-3 text-sm">
                                    {remainingTime.days > 0 && (
                                      <span className="flex items-center gap-1">
                                        <span className="text-orange-600">
                                          📅
                                        </span>
                                        <span className="font-semibold">
                                          {remainingTime.days}d
                                        </span>
                                      </span>
                                    )}
                                    {remainingTime.hours > 0 && (
                                      <span className="flex items-center gap-1">
                                        <span className="text-orange-600">
                                          🕐
                                        </span>
                                        <span className="font-semibold">
                                          {remainingTime.hours}h
                                        </span>
                                      </span>
                                    )}
                                    <span className="flex items-center gap-1">
                                      <span className="text-orange-600">
                                        ⏱️
                                      </span>
                                      <span className="font-semibold">
                                        {remainingTime.minutes}m
                                      </span>
                                    </span>
                                  </div>
                                </div>
                              )}
                              {remainingTime.isExpired && (
                                <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-200">
                                  <p className="text-xs text-red-700 font-medium">
                                    ⚠️ Reservation has expired
                                  </p>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Method - Right Side */}
              {userStateInfo.has_payment_card ? (
                <div className="border rounded-lg p-6 bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <h5 className="font-semibold text-gray-800 text-lg">
                      Payment Method
                    </h5>
                  </div>

                  {/* Show default payment card */}
                  {userStateInfo.has_payment_card.payment_methods.map(
                    (card: any, index: number) => (
                      <div
                        key={index}
                        className={`bg-white rounded-lg p-4 border ${
                          card.is_default
                            ? "border-green-200 bg-green-50"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-2xl">
                            {card.card.brand === "visa"
                              ? "💳"
                              : card.card.brand === "mastercard"
                              ? "💳"
                              : card.card.brand === "amex"
                              ? "💳"
                              : "💳"}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-lg font-bold text-blue-900">
                                {card.card.display_brand || card.card.brand}
                              </span>
                              <span className="tracking-widest font-mono text-base text-gray-700">
                                **** **** **** {card.card.last4}
                              </span>
                              <span className="text-xs text-gray-500">
                                Expires {card.card.exp_month}/
                                {card.card.exp_year}
                              </span>
                            </div>
                            {card.is_default && (
                              <div className="flex items-center gap-2">
                                <span className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full font-medium">
                                  Default Payment Method
                                </span>
                                <span className="text-sm text-green-700 font-medium flex items-center gap-1">
                                  <span className="text-green-500">✓</span>{" "}
                                  Ready for payment
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div className="border rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <h5 className="font-semibold text-gray-800 text-lg">
                      Payment Required
                    </h5>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <p className="text-sm text-gray-600 mb-4">
                      You need to add a payment method to complete your
                      reservation payment.
                    </p>

                    {/* Show existing payment methods when no new card was just added */}
                    {cards.length > 0 && !cardAdded && (
                      <div className="space-y-3 mb-4">
                        <h6 className="font-medium text-gray-800">
                          Your Current Payment Method
                        </h6>
                        {cards.map((card: any, index: number) => (
                          <div
                            key={index}
                            className={`border rounded-lg p-4 ${
                              card.is_default
                                ? "bg-green-50 border-green-200"
                                : "bg-white border-gray-200"
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <span className="text-lg font-bold text-blue-900">
                                {card.card.display_brand || card.card.brand}
                              </span>
                              <span className="tracking-widest font-mono text-base text-gray-700">
                                **** **** **** {card.card.last4}
                              </span>
                              <span className="text-xs text-gray-500">
                                Expires {card.card.exp_month}/
                                {card.card.exp_year}
                              </span>
                              {card.is_default && (
                                <span className="ml-auto px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                                  Default
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Show message when no cards exist */}
                    {cards.length === 0 && (
                      <div className="border rounded-lg p-4 mb-4 bg-yellow-50 border-yellow-200">
                        <div className="font-semibold mb-2 text-yellow-800">
                          No Payment Method
                        </div>
                        <p className="text-sm text-yellow-700">
                          Please add a payment method to continue with your
                          purchase.
                        </p>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="space-y-3">
                      {/* Card Replacement Progress Indicator */}
                      {isReplacingCardLoading && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            <span className="text-sm font-medium text-blue-800">
                              Processing Card Replacement...
                            </span>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-xs text-blue-700">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <span>Setting new card as default</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-blue-700">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <span>Removing old payment methods</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-blue-700">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <span>Updating account settings</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Show newly added card with enhanced styling */}
            {cardAdded && newlyAddedCard && (
              <div className="bg-white rounded-lg p-4 border-2 border-green-200 shadow-sm animate-pulse">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
                  <h6 className="font-semibold text-green-800">
                    🎉{" "}
                    {isReplacingCard
                      ? "Payment Method Updated Successfully!"
                      : "Payment Method Added Successfully!"}
                  </h6>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                  <div className="text-3xl">
                    {newlyAddedCard.card.brand === "visa"
                      ? "💳"
                      : newlyAddedCard.card.brand === "mastercard"
                      ? "💳"
                      : newlyAddedCard.card.brand === "amex"
                      ? "💳"
                      : "💳"}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-lg font-bold text-blue-900">
                        {newlyAddedCard.card.display_brand ||
                          newlyAddedCard.card.brand}
                      </span>
                      <span className="tracking-widest font-mono text-base text-gray-700">
                        **** **** **** {newlyAddedCard.card.last4}
                      </span>
                      <span className="text-xs text-gray-500">
                        Expires {newlyAddedCard.card.exp_month}/
                        {newlyAddedCard.card.exp_year}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full font-medium">
                        Default Payment Method
                      </span>
                      <span className="text-sm text-green-700 font-medium flex items-center gap-1">
                        <span className="text-green-500">✓</span> Ready for
                        payment
                      </span>
                    </div>
                  </div>
                </div>

                {/* Show package and number info with the new card */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-blue-600">📦</span>
                    <span className="text-sm font-medium text-blue-800">
                      Your Reserved Package
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {userStateInfo.has_reserved?.package.name}
                      </p>
                      <p className="text-sm text-gray-600">
                        {userStateInfo.has_reserved?.package.minutes} minutes
                        included
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-gray-900">
                        ${userStateInfo.has_reserved?.package.price}
                      </p>
                      <p className="text-xs text-gray-500">per month</p>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600">📞</span>
                      <span className="text-sm text-gray-600">
                        Phone Number:{" "}
                      </span>
                      <span className="font-semibold text-gray-900">
                        {formatPhoneNumberForDisplay(
                          userStateInfo.has_reserved?.phone_number || ""
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <AddPaymentCardDialog
              isOpen={showAddCard}
              onClose={() => {
                setShowAddCard(false);
                setIsReplacingCard(false);
                setIsReplacingCardLoading(false);
                setCurrentCardId(undefined); // Reset the current card ID
                setIsDialogOpen(false); // Reset dialog flag when user closes manually
              }}
              onSuccess={() => {
                setShowAddCard(false);
                // Don't reset isDialogOpen here - it will be reset in handlePaymentCardComplete's finally block
                handlePaymentCardComplete();
              }}
              isReplacing={isReplacingCard}
              onCardReplacement={(newCardId, oldCardId) =>
                handleEnhancedCardReplacement(newCardId, oldCardId)
              }
              isLoading={isReplacingCardLoading}
              oldCardId={currentCardId}
              hasExistingBillingAddress={cards.length > 0}
              {...({
                businessAddress: {
                  line1: line1,
                  line2: null,
                  city: city,
                  state: state,
                  postal_code: zipCode,
                  country: country,
                  firstName: firstName,
                  lastName: lastName,
                },
              } as any)}
            />

            {/* Card Replacement Confirmation Dialog */}
            {showReplaceConfirmation && (
              <div
                className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
                onClick={(e) => {
                  // Close dialog when clicking on backdrop
                  if (e.target === e.currentTarget) {
                    cancelCardReplacement();
                  }
                }}
              >
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                  {/* Close button */}
                  <div className="flex justify-end mb-2">
                    <button
                      type="button"
                      onClick={cancelCardReplacement}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="Close dialog"
                    >
                      <FiX size={24} />
                    </button>
                  </div>

                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-orange-600 text-2xl">⚠️</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Confirm Card Replacement
                    </h3>
                    <p className="text-sm text-gray-600">
                      Are you sure you want to replace your current payment
                      method? This action will:
                    </p>
                  </div>

                  <div className="mb-6">
                    <ul className="text-sm text-gray-700 space-y-2 text-left">
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <span>Add your new card to your account</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <span>
                          Set the new card as your default payment method
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 mt-0.5">⚠</span>
                        <span>
                          <strong>Remove all existing payment methods</strong>
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">ℹ</span>
                        <span>Update your account settings</span>
                      </li>
                    </ul>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={cancelCardReplacement}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmCardReplacement}
                      className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"
                    >
                      Confirm Replacement
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "payment_card":
        return (
          <div className="space-y-6">
            <div className="text-center">
              <FiCreditCard size={48} className="mx-auto text-gray-400 mb-4" />
              <h4 className="font-medium mb-2">Add Payment Method</h4>
              <p className="text-sm text-gray-600 mb-4">
                You need to add a payment method to continue
              </p>
            </div>

            {/* Side by side layout for order summary and payment method */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Order Summary - Left Side */}
              {((selectedPackage && selectedNumber) ||
                userStateInfo.has_reserved) && (
                <div
                  className="border rounded-lg p-6 bg-gradient-to-r from-blue-50 to-green-50 border-blue-200"
                  data-order-summary
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <h5 className="font-semibold text-gray-800 text-lg">
                        Order Summary
                      </h5>
                    </div>
                    {/* Show Manage Reservation button only when user has reservation */}
                    {userStateInfo.has_reserved && (
                      <button
                        type="button"
                        onClick={handleShowReservationActions}
                        className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Manage Reservation
                      </button>
                    )}
                  </div>

                  {/* Package Details */}
                  <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h6 className="font-semibold text-gray-900 text-base">
                          {userStateInfo.has_reserved
                            ? userStateInfo.has_reserved.package.name
                            : selectedPackage.name}
                        </h6>
                        <p className="text-sm text-gray-600">
                          {userStateInfo.has_reserved
                            ? `${userStateInfo.has_reserved.package.minutes} minutes included`
                            : selectedPackage.description}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {userStateInfo.has_reserved
                            ? `${userStateInfo.has_reserved.package.minutes} minutes included`
                            : `${selectedPackage.minutes} minutes included`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-xl text-gray-900">
                          $
                          {userStateInfo.has_reserved
                            ? userStateInfo.has_reserved.package.price
                            : selectedPackage.price}
                        </p>
                        <p className="text-xs text-gray-500">per month</p>
                      </div>
                    </div>

                    {/* Phone Number */}
                    <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-sm font-medium">
                          📞
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          {userStateInfo.has_reserved ? "Reserved" : "Selected"}{" "}
                          Phone Number
                        </p>
                        <p className="font-semibold text-gray-900">
                          {userStateInfo.has_reserved
                            ? formatPhoneNumberForDisplay(
                                userStateInfo.has_reserved.phone_number
                              )
                            : formatPhoneNumberForDisplay(selectedNumber || "")}
                        </p>
                      </div>
                    </div>

                    {/* Show reservation expiration if user has reservation */}
                    {userStateInfo.has_reserved &&
                      (() => {
                        const remainingTime = calculateRemainingTime(
                          userStateInfo.has_reserved.expires_at
                        );
                        return (
                          <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                              <span className="text-orange-600 text-sm font-medium">
                                ⏰
                              </span>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-gray-600">
                                Reservation Expires
                              </p>
                              <p className="font-semibold text-red-500">
                                {new Date(
                                  userStateInfo.has_reserved.expires_at
                                ).toLocaleString()}
                              </p>
                              {!remainingTime.isExpired && (
                                <div className="mt-2 p-2 bg-orange-50 rounded-lg border border-orange-200">
                                  <p className="text-xs text-orange-700 font-medium mb-1">
                                    Time Remaining:
                                  </p>
                                  <div className="flex items-center gap-3 text-sm">
                                    {remainingTime.days > 0 && (
                                      <span className="flex items-center gap-1">
                                        <span className="text-orange-600">
                                          📅
                                        </span>
                                        <span className="font-semibold">
                                          {remainingTime.days}d
                                        </span>
                                      </span>
                                    )}
                                    {remainingTime.hours > 0 && (
                                      <span className="flex items-center gap-1">
                                        <span className="text-orange-600">
                                          🕐
                                        </span>
                                        <span className="font-semibold">
                                          {remainingTime.hours}h
                                        </span>
                                      </span>
                                    )}
                                    <span className="flex items-center gap-1">
                                      <span className="text-orange-600">
                                        ⏱️
                                      </span>
                                      <span className="font-semibold">
                                        {remainingTime.minutes}m
                                      </span>
                                    </span>
                                  </div>
                                </div>
                              )}
                              {remainingTime.isExpired && (
                                <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-200">
                                  <p className="text-xs text-red-700 font-medium">
                                    ⚠️ Reservation has expired
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                  </div>
                </div>
              )}

              {/* Payment Method Section - Right Side */}
              <div className="space-y-4">
                {/* Show existing payment methods - Always show when cards exist */}
                {cards.length > 0 && (
                  <div
                    className={`border rounded-lg p-6 ${
                      cardAdded
                        ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 shadow-md"
                        : "bg-gradient-to-r from-green-50 to-blue-50 border-green-200"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          cardAdded
                            ? "bg-green-600 animate-pulse"
                            : "bg-green-500"
                        }`}
                      ></div>
                      <h5 className="font-semibold text-gray-800 text-lg">
                        {cardAdded
                          ? "✅ Payment Method Added!"
                          : "Current Payment Method"}
                      </h5>
                    </div>
                    {cards.map((card: any, index: number) => (
                      <div
                        key={index}
                        className={`bg-white rounded-lg p-4 border ${
                          card.is_default
                            ? "bg-green-50 border-green-200"
                            : "bg-white border-gray-200"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <span className="text-lg font-bold text-blue-900">
                            {card.card.display_brand || card.card.brand}
                          </span>
                          <span className="tracking-widest font-mono text-base text-gray-700">
                            **** **** **** {card.card.last4}
                          </span>
                          <span className="text-xs text-gray-500">
                            Expires {card.card.exp_month}/{card.card.exp_year}
                          </span>
                          {card.is_default && (
                            <span className="ml-auto px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                              Default
                            </span>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Replace Payment Method Button - Below Current Payment Method */}
                    <button
                      type="button"
                      onClick={(e) => {
                        console.log(
                          "🔍 DEBUG - Replace Payment Method button clicked"
                        );
                        handleReplaceCard(e);
                      }}
                      disabled={isReplacingCardLoading}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-md hover:shadow-lg mt-4"
                    >
                      {isReplacingCardLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                          Replace Payment Method
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Show message when no cards exist */}
                {cards.length === 0 && (
                  <div className="border rounded-lg p-6 bg-yellow-50 border-yellow-200">
                    <div className="font-semibold mb-2 text-yellow-800">
                      No Payment Method
                    </div>
                    <p className="text-sm text-yellow-700 mb-4">
                      Please add a payment method to continue with your
                      purchase.
                    </p>

                    {/* Add Payment Method Button - When no cards exist */}
                    <button
                      type="button"
                      onClick={(e) => {
                        console.log(
                          "🔍 DEBUG - Add Payment Method button clicked"
                        );
                        handleReplaceCard(e);
                      }}
                      disabled={isReplacingCardLoading}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-md hover:shadow-lg"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Add Payment Method
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Card Replacement Information */}
            {cards.length > 0 && (
              <div className="border rounded-lg p-4 mb-6 bg-blue-50 border-blue-200">
                <div className="font-semibold mb-2 text-blue-800 flex items-center gap-2">
                  <FiInfo className="text-blue-600" />
                  About Card Replacement
                </div>
                <div className="text-sm text-blue-700 space-y-2">
                  <p>
                    When you replace your payment method, the following steps
                    will occur:
                  </p>
                  <ol className="list-decimal list-inside space-y-1 ml-2">
                    <li>Your new card will be added to your account</li>
                    <li>
                      The new card will be set as your default payment method
                    </li>
                    <li>All old payment methods will be removed</li>
                    <li>Your account settings will be updated</li>
                  </ol>
                  <p className="text-xs text-blue-600 mt-2">
                    <strong>Note:</strong> This process is irreversible. Make
                    sure you have your new card details ready.
                  </p>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="space-y-3">
              {/* Card Replacement Progress Indicator */}
              {isReplacingCardLoading && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span className="text-sm font-medium text-blue-800">
                      Processing Card Replacement...
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs text-blue-700">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>Setting new card as default</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-blue-700">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>Removing old payment methods</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-blue-700">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>Updating account settings</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <AddPaymentCardDialog
              isOpen={showAddCard}
              onClose={() => {
                setShowAddCard(false);
                setIsReplacingCard(false);
                setIsReplacingCardLoading(false);
                setCurrentCardId(undefined);
                setIsDialogOpen(false); // Reset dialog flag when user closes manually
              }}
              onSuccess={() => {
                setShowAddCard(false);
                // Don't reset isDialogOpen here - it will be reset in handlePaymentCardComplete's finally block
                handlePaymentCardComplete();
              }}
              isReplacing={isReplacingCard}
              onCardReplacement={(newCardId, oldCardId) =>
                handleEnhancedCardReplacement(newCardId, oldCardId)
              }
              isLoading={isReplacingCardLoading}
              oldCardId={currentCardId}
              hasExistingBillingAddress={cards.length > 0}
              {...({
                businessAddress: {
                  line1: line1,
                  line2: null,
                  city: city,
                  state: state,
                  postal_code: zipCode,
                  country: country,
                  firstName: firstName,
                  lastName: lastName,
                },
              } as any)}
            />

            {/* Card Replacement Confirmation Dialog */}
            {showReplaceConfirmation && (
              <div
                className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
                onClick={(e) => {
                  // Close dialog when clicking on backdrop
                  if (e.target === e.currentTarget) {
                    cancelCardReplacement();
                  }
                }}
              >
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                  {/* Close button */}
                  <div className="flex justify-end mb-2">
                    <button
                      type="button"
                      onClick={cancelCardReplacement}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="Close dialog"
                    >
                      <FiX size={24} />
                    </button>
                  </div>

                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-orange-600 text-2xl">⚠️</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Confirm Card Replacement
                    </h3>
                    <p className="text-sm text-gray-600">
                      Are you sure you want to replace your current payment
                      method? This action will:
                    </p>
                  </div>

                  <div className="mb-6">
                    <ul className="text-sm text-gray-700 space-y-2 text-left">
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <span>Add your new card to your account</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <span>
                          Set the new card as your default payment method
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 mt-0.5">⚠</span>
                        <span>
                          <strong>Remove all existing payment methods</strong>
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">ℹ</span>
                        <span>Update your account settings</span>
                      </li>
                    </ul>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={cancelCardReplacement}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmCardReplacement}
                      className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"
                    >
                      Confirm Replacement
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "complete":
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <FiCheck size={32} className="text-green-600" />
              </div>
              <div className="mt-4">
                <h4 className="font-medium text-lg mb-2">Setup Complete!</h4>
                <p className="text-sm text-gray-600">
                  Your account has been successfully configured and your
                  subscription is now active
                </p>
              </div>
            </div>

            {(completedPackageInfo || userStateInfo.has_reserved) && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                  <FiPackage className="mr-2" />
                  Package Details
                </h5>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Package Name:</span>
                    <p className="font-medium text-gray-900">
                      {
                        (completedPackageInfo || userStateInfo.has_reserved)
                          ?.package.name
                      }
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Phone Number:</span>
                    <p className="font-medium text-gray-900">
                      {formatPhoneNumberForDisplay(
                        (completedPackageInfo || userStateInfo.has_reserved)
                          ?.phone_number || ""
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Minutes:</span>
                    <p className="font-medium text-gray-900">
                      {
                        (completedPackageInfo || userStateInfo.has_reserved)
                          ?.package.minutes
                      }{" "}
                      minutes
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Type:</span>
                    <p className="font-medium text-gray-900 capitalize">
                      {
                        (completedPackageInfo || userStateInfo.has_reserved)
                          ?.package.type
                      }
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Price:</span>
                    <p className="font-medium text-gray-900">
                      $
                      {
                        (completedPackageInfo || userStateInfo.has_reserved)
                          ?.package.price
                      }{" "}
                      {(
                        completedPackageInfo || userStateInfo.has_reserved
                      )?.package.currency.toUpperCase()}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Billing Cycle:</span>
                    <p className="font-medium text-gray-900">
                      {
                        (completedPackageInfo || userStateInfo.has_reserved)
                          ?.package.recurring.interval_count
                      }{" "}
                      {
                        (completedPackageInfo || userStateInfo.has_reserved)
                          ?.package.recurring.interval
                      }
                      {(completedPackageInfo || userStateInfo.has_reserved)
                        ?.package.recurring.interval_count > 1
                        ? "s"
                        : ""}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Reservation ID:</span>
                    <p className="font-medium text-gray-900 text-xs">
                      {
                        (completedPackageInfo || userStateInfo.has_reserved)
                          ?.reservation_id
                      }
                    </p>
                  </div>
                  {(completedPackageInfo || userStateInfo.has_reserved)
                    ?.expires_at && (
                    <div>
                      <span className="text-gray-600">Expires:</span>
                      <p className="font-medium text-gray-900">
                        {new Date(
                          (
                            completedPackageInfo || userStateInfo.has_reserved
                          )?.expires_at
                        ).toLocaleString()}
                      </p>
                      {(() => {
                        const remainingTime = calculateRemainingTime(
                          (completedPackageInfo || userStateInfo.has_reserved)
                            ?.expires_at
                        );
                        return (
                          <>
                            {!remainingTime.isExpired && (
                              <div className="mt-2 p-2 bg-orange-50 rounded-lg border border-orange-200">
                                <p className="text-xs text-orange-700 font-medium mb-1">
                                  Time Remaining:
                                </p>
                                <div className="flex items-center gap-3 text-sm">
                                  {remainingTime.days > 0 && (
                                    <span className="flex items-center gap-1">
                                      <span className="text-orange-600">
                                        📅
                                      </span>
                                      <span className="font-semibold">
                                        {remainingTime.days}d
                                      </span>
                                    </span>
                                  )}
                                  {remainingTime.hours > 0 && (
                                    <span className="flex items-center gap-1">
                                      <span className="text-orange-600">
                                        🕐
                                      </span>
                                      <span className="font-semibold">
                                        {remainingTime.hours}h
                                      </span>
                                    </span>
                                  )}
                                  <span className="flex items-center gap-1">
                                    <span className="text-orange-600">⏱️</span>
                                    <span className="font-semibold">
                                      {remainingTime.minutes}m
                                    </span>
                                  </span>
                                </div>
                              </div>
                            )}
                            {remainingTime.isExpired && (
                              <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-200">
                                <p className="text-xs text-red-700 font-medium">
                                  ⚠️ Reservation has expired
                                </p>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            )}

            {paymentResultData ? (
              <div
                className={`rounded-lg p-4 border ${
                  paymentResultData.detail.error ||
                  paymentResultData.detail.charges.some(
                    (c: any) => c.status === "failed"
                  )
                    ? "bg-red-50 border-red-200"
                    : "bg-blue-50 border-blue-200"
                }`}
              >
                <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                  <FiCreditCard className="mr-2" />
                  {paymentResultData.detail.error
                    ? "Payment Error"
                    : "Payment Details"}
                </h5>

                {paymentResultData.detail.error && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md">
                    <p className="text-red-800 text-sm font-medium">
                      {paymentResultData.detail.msg || "Payment failed"}
                    </p>
                    {typeof paymentResultData.detail.error === "string" && (
                      <p className="text-red-700 text-xs mt-1">
                        {paymentResultData.detail.error}
                      </p>
                    )}
                    {typeof paymentResultData.detail.error === "object" && (
                      <div className="mt-2">
                        <p className="text-red-700 text-xs font-medium mb-1">
                          Error Details:
                        </p>
                        <pre className="text-red-700 text-xs bg-red-50 p-2 rounded border overflow-x-auto">
                          {JSON.stringify(
                            paymentResultData.detail.error,
                            null,
                            2
                          )}
                        </pre>
                      </div>
                    )}
                  </div>
                )}

                {/* Payment Status Summary */}
                {paymentResultData.detail.charges.length > 0 && (
                  <div className="mb-4 p-3 bg-gray-100 border border-gray-300 rounded-md">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700 font-medium">
                        Payment Summary:
                      </span>
                      <span
                        className={`px-2 py-1 text-xs rounded-full font-medium ${
                          paymentResultData.detail.charges.every(
                            (c: any) => c.status === "succeeded"
                          )
                            ? "bg-green-100 text-green-800"
                            : paymentResultData.detail.charges.every(
                                (c: any) => c.status === "failed"
                              )
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {paymentResultData.detail.charges.every(
                          (c: any) => c.status === "succeeded"
                        )
                          ? "All Successful"
                          : paymentResultData.detail.charges.every(
                              (c: any) => c.status === "failed"
                            )
                          ? "All Failed"
                          : "Partial Success"}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {
                        paymentResultData.detail.charges.filter(
                          (c: any) => c.status === "succeeded"
                        ).length
                      }{" "}
                      successful,{" "}
                      {
                        paymentResultData.detail.charges.filter(
                          (c: any) => c.status === "failed"
                        ).length
                      }{" "}
                      failed
                    </div>
                  </div>
                )}

                {/* Individual Charge Details */}
                {paymentResultData.detail.charges.length > 0 && (
                  <div className="space-y-4">
                    {paymentResultData.detail.charges.map(
                      (charge: any, index: number) => (
                        <div
                          key={index}
                          className={`p-3 border rounded-md ${
                            charge.status === "succeeded"
                              ? "bg-green-50 border-green-200"
                              : "bg-red-50 border-red-200"
                          }`}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-700">
                              Charge #{index + 1}
                            </span>
                            <span
                              className={`px-2 py-1 text-xs rounded-full font-medium ${
                                charge.status === "succeeded"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {charge.status === "succeeded"
                                ? "Success"
                                : "Failed"}
                            </span>
                          </div>

                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Amount:</span>
                              <span className="font-medium text-gray-900">
                                ${charge.amount || "0"}{" "}
                                {(charge.currency || "usd").toUpperCase()}
                              </span>
                            </div>

                            {charge.payment_method?.card && (
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600">
                                  Payment Method:
                                </span>
                                <span className="font-medium text-gray-900">
                                  {(
                                    charge.payment_method.card.brand ||
                                    "Unknown"
                                  ).toUpperCase()}{" "}
                                  ••••{" "}
                                  {charge.payment_method.card.last4 || "****"}
                                </span>
                              </div>
                            )}

                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">
                                Transaction ID:
                              </span>
                              <span className="font-medium text-gray-900 text-xs">
                                {charge.id || "N/A"}
                              </span>
                            </div>

                            {charge.payment_intent && (
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600">
                                  Payment Intent:
                                </span>
                                <span className="font-medium text-gray-900 text-xs">
                                  {charge.payment_intent || "N/A"}
                                </span>
                              </div>
                            )}

                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">
                                Date & Time:
                              </span>
                              <span className="font-medium text-gray-900 text-xs">
                                {charge.created
                                  ? new Date(charge.created).toLocaleString()
                                  : "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}

                <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <p className="text-sm text-gray-700">
                    {paymentResultData.detail.msg}
                  </p>
                </div>

                {(paymentResultData.detail.error ||
                  paymentResultData.detail.charges.some(
                    (c: any) => c.status === "failed"
                  )) && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <h6 className="text-sm font-medium text-red-800 mb-2">
                      What This Means:
                    </h6>
                    <ul className="text-xs text-red-700 space-y-1">
                      <li>
                        • Your reservation is still active and will expire at
                        the scheduled time
                      </li>
                      <li>
                        • You can retry the payment using the button below
                      </li>
                      <li>
                        • If the problem persists, please check your payment
                        method or contact support
                      </li>
                    </ul>
                  </div>
                )}

                {/* Retry Button for Failed Payments */}
                {(paymentResultData.detail.error ||
                  paymentResultData.detail.charges.some(
                    (c: any) => c.status === "failed"
                  )) && (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={handlePaymentRetry}
                      disabled={isLoading}
                      className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      {isLoading ? "Retrying..." : "Retry Payment"}
                    </button>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      If the problem persists, please check your payment method
                      or contact support.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                  <FiInfo className="mr-2" />
                  Payment Information
                </h5>
                <p className="text-sm text-gray-600">
                  Payment details will be displayed here after payment
                  processing.
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-green-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              Complete Your Setup
              <span className="text-blue-600">✨</span>
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Follow the steps to complete your account setup
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-green-50 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">
              {headerSteps.length > 0
                ? `Step ${getCurrentStepIndex() + 1} of ${headerSteps.length}`
                : "Loading..."}
            </span>
            <span className="text-sm font-medium text-blue-600">
              {headerSteps.length > 0
                ? `${Math.round(getProgressPercentage())}% Complete`
                : "0% Complete"}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
            <div
              className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500 ease-out shadow-md"
              style={{ width: `${getProgressPercentage()}%` }}
            ></div>
          </div>
        </div>

        {/* Steps Indicator */}
        <div className="px-6 py-6 border-b border-gray-200 overflow-hidden bg-white flex ">
          <div className="flex items-center space-x-4 min-w-max">
            {headerSteps.map((step) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
                    step.isActive
                      ? "border-blue-500 bg-gradient-to-r from-blue-500 to-green-500 text-white shadow-lg scale-110"
                      : step.isCompleted
                      ? "border-green-500 bg-green-500 text-white"
                      : "border-gray-300 bg-white text-gray-400"
                  }`}
                >
                  {step.isCompleted ? (
                    <FiCheck size={18} className="font-bold" />
                  ) : (
                    <span className="text-sm font-bold">{step.index + 1}</span>
                  )}
                </div>
                <div className="ml-3">
                  <p
                    className={`text-sm font-semibold ${
                      step.isActive
                        ? "text-blue-600"
                        : step.isCompleted
                        ? "text-green-600"
                        : "text-gray-500"
                    }`}
                  >
                    {step.title}
                  </p>
                  <p className="text-xs text-gray-500">{step.description}</p>
                </div>
                {step.index < headerSteps.length - 1 && (
                  <div
                    className={`ml-4 w-12 h-0.5 transition-colors ${
                      step.isCompleted ? "bg-green-500" : "bg-gray-300"
                    }`}
                  ></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">{renderStepContent()}</div>

        {/* Sticky Footer Navigation */}
        <div className="sticky bottom-0 left-0 right-0 px-6 py-4 border-t-2 border-gray-200 bg-white shadow-2xl flex justify-between items-center z-20">
          {currentStep !== "complete" ? (
            <>
              {/* Show loading state when processing payment */}
              {isLoading && currentStep === "payment_card" ? (
                <div className="flex-1 flex items-center justify-center gap-4 py-2">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
                    <div className="flex flex-col">
                      <span className="text-base font-semibold text-gray-900">
                        Processing Your Setup...
                      </span>
                      <span className="text-sm text-gray-600">
                        Please wait while we complete your subscription
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Previous Button */}
                  {getCurrentStepIndex() > 0 ? (
                    <button
                      type="button"
                      onClick={handlePrevious}
                      disabled={isLoading}
                      className="flex items-center gap-2 px-5 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-md hover:shadow-lg"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                      Previous
                    </button>
                  ) : (
                    <div />
                  )}

                  {/* Step Counter */}
                  <div className="text-sm font-bold text-gray-600">
                    Step{" "}
                    <span className="text-blue-600">
                      {getCurrentStepIndex() + 1}
                    </span>{" "}
                    of <span className="text-gray-800">{steps.length}</span>
                  </div>
                </>
              )}

              {/* Next Button - Only for personal_data and package_selection steps */}
              {!isLoading &&
                (currentStep === "personal_data" ||
                  currentStep === "package_selection") && (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={!canGoNext() || isLoading}
                    className="flex items-center gap-2 px-6 py-3 text-white bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    Next
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                )}

              {/* Complete Button - For payment_card step (Step 3) */}
              {!isLoading &&
                currentStep === "payment_card" &&
                cards.length > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      if (e) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                      handleCompleteAllAPIs();
                    }}
                    disabled={isLoading}
                    className="flex items-center gap-2 px-6 py-3 text-white bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Complete Setup
                  </button>
                )}

              {!isLoading && currentStep === "reservation" && (
                <button
                  type="button"
                  onClick={handleReservationPayment}
                  disabled={
                    isLoading ||
                    (!userStateInfo.has_payment_card && cards.length === 0)
                  }
                  className="flex items-center gap-2 px-6 py-3 text-white bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Complete Payment
                    </>
                  )}
                </button>
              )}
            </>
          ) : (
            /* Footer for Complete Step */
            <button
              type="button"
              onClick={onComplete}
              className="w-full bg-gradient-to-r from-green-500 to-blue-500 text-white py-3 px-6 rounded-xl hover:from-green-600 hover:to-blue-600 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center gap-2"
            >
              <span>Continue to Dashboard</span>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Global Reservation Actions Modal - Shows in any step when user has reservation */}
        {showReservationActions && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Manage Reservation</h3>
                <button
                  type="button"
                  onClick={() => setShowReservationActions(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close reservation actions"
                >
                  <FiX size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  What would you like to do with your reservation?
                </p>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleChangePackage}
                    className="w-full p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-lg">📦</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Change Package
                        </h4>
                        <p className="text-sm text-gray-600">
                          Select a different package for your phone number
                        </p>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirmation(true)}
                    className="w-full p-4 border border-gray-200 rounded-lg hover:border-red-300 hover:bg-red-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                        <span className="text-red-600 text-lg">🗑️</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Delete Reservation
                        </h4>
                        <p className="text-sm text-gray-600">
                          Cancel your reservation and release the phone number
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Global Delete Confirmation Modal */}
        {showDeleteConfirmation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-red-600 text-2xl">⚠️</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Delete Reservation?
                </h3>
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete your reservation? This action
                  cannot be undone and will release your phone number.{" "}
                  <strong>
                    {formatPhoneNumberForDisplay(
                      userStateInfo.has_reserved?.phone_number || ""
                    )}
                  </strong>
                </p>
              </div>

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={handleDeleteReservation}
                  disabled={isDeletingReservation}
                  className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isDeletingReservation
                    ? "Deleting..."
                    : "Yes, Delete Reservation"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmation(false)}
                  disabled={isDeletingReservation}
                  className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Global Package Change Modal */}
        {showPackageChange && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Change Package</h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowPackageChange(false);
                    setSelectedNewPackage(null);
                    setAvailablePackages([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close package change"
                >
                  <FiX size={24} />
                </button>
              </div>

              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-blue-600">📞</span>
                    <span className="font-medium text-blue-800">
                      Phone Number
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatPhoneNumberForDisplay(
                      userStateInfo.has_reserved?.phone_number || ""
                    )}
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-4">
                    Available Packages
                  </h4>

                  {isLoadingPackages ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-gray-600 mt-2">Loading packages...</p>
                    </div>
                  ) : availablePackages.length > 0 ? (
                    <div className="space-y-3">
                      {availablePackages.map((pkg) => {
                        const isCurrentPackage =
                          pkg.id === userStateInfo.has_reserved?.package?.id;
                        return (
                          <div
                            key={pkg.id}
                            onClick={() =>
                              !isCurrentPackage && setSelectedNewPackage(pkg)
                            }
                            className={`border rounded-lg p-4 transition-colors ${
                              isCurrentPackage
                                ? "border-gray-300 bg-gray-100 cursor-not-allowed opacity-60"
                                : selectedNewPackage?.id === pkg.id
                                ? "border-blue-500 bg-blue-50 cursor-pointer"
                                : "border-gray-200 hover:border-gray-300 cursor-pointer"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h5 className="font-semibold text-gray-900">
                                    {pkg.name}
                                  </h5>
                                  {isCurrentPackage && (
                                    <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full font-medium">
                                      Current Package
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">
                                  {pkg.minutes} minutes included
                                </p>
                                <p className="text-xs text-gray-500">
                                  ${pkg.fee_per_overage_minute} per additional
                                  minute
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-lg text-gray-900">
                                  ${pkg.price}
                                </p>
                                <p className="text-xs text-gray-500">
                                  per month
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-600">
                        No packages available for this phone number type.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPackageChange(false);
                      setSelectedNewPackage(null);
                      setAvailablePackages([]);
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handlePackageChangeConfirm}
                    disabled={
                      !selectedNewPackage ||
                      isChangingPackage ||
                      selectedNewPackage?.id ===
                        userStateInfo.has_reserved?.package?.id
                    }
                    className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {isChangingPackage ? "Changing..." : "Change Package"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
